import type { TmyHottestHoursSummary } from './tmy';
import type { NrcDailyPowerSummary } from '../lib/nrcReactorStatus';
/** A quoted source fact accessible to every analysis; never an automatic design/financial selection. */
export interface DocumentEvidenceFact {
  documentId: string;
  documentName: string;
  label: string;
  value: string;
  unit?: string;
  sourceQuote: string;
  charStart: number;
  charEnd: number;
  page?: number;
  sedSections: SedSectionId[];
  requirementIds: string[];
  attribution?: EvidenceAttribution;
  confidence: number;
  channel: 'document' | 'vision';
  adoption: 'source-statement';
}

/**
 * Site Evidence Document (SED) — the single source of truth for site assessment.
 *
 * Every field traces to a specific API call at a specific time.
 * Missing data is UNAVAILABLE, never estimated.
 * Anomalies are flagged for human review.
 * Versions are immutable — corrections create amendments, not overwrites.
 */

/** Status of a data section — verified data vs. unavailable.
 *
 *   - `'verified'`             — provider returned a usable value.
 *   - `'verified-from-doc'`    — a user-uploaded doc supplied the value
 *     via `applyDocAwareConfidence` (Documents_On_Site Prompt 11).
 *   - `'verified-from-context'`— UAE-1: a user-authored claim
 *     (`ExtractedClaim.requirementId`) supplied the value. Slots in
 *     BELOW `'verified-from-doc'` in the confidence ladder (authored
 *     low=0.45, med=0.6, high=0.75; vs DOC_FALLBACK_CONFIDENCE=0.8).
 *   - `'verified-from-report'` — RDX-1: a prior generated report on this
 *     site supplied the value via `applyReportToSed`. Ranks between
 *     `'verified-from-context'` (user-authored, more authoritative) and
 *     `'verified-from-doc'` (uploaded document) when the resolver
 *     adopts a report-derived value. Optional / forward-looking:
 *     legacy SEDs do not carry this status because the
 *     report → SED feedback loop did not exist before RDX.
 *   - `'verified-from-research'` — DCO-1: a per-slot grounded AI
 *     Research call (`dispatchResearch`) supplied the value. Only
 *     written by the Data Completion Orchestrator AFTER provider /
 *     doc / DAM tiers have been exhausted for the slot. Carries
 *     `provenanceDetail.researchSource` with `sourceUrl`,
 *     `sourceTitle`, `retrievedAt`, `confidence`, and the
 *     groundingChunks the model used to produce the answer. Optional
 *     / forward-looking: legacy SEDs do not carry this status.
 *   - `'verified-from-dam'`    — DCO-1 (Wave B): a curated Datacrate
 *     Model regulatory/benchmark entry supplied the value via
 *     `applyDamToSlot`. Ranks above `'verified-from-research'`
 *     (curated > googled). Optional / forward-looking; Wave B is
 *     not yet wired into the orchestrator's live path.
 *   - `'anomalous'`            — data present but flagged by a cross-check.
 *   - `'unavailable'`          — provider failed AND no doc / context filled in.
 */
export type EvidenceStatus =
  | 'verified'
  | 'unavailable'
  | 'anomalous'
  | 'verified-from-doc'
  | 'verified-from-context'
  | 'verified-from-report'
  | 'verified-from-research'
  | 'verified-from-dam'
  | 'verified-from-intent';

/**
 * R-71 — provider outcome status for the pre-generation coverage gate.
 * Derived from persisted SED section payloads and bootstrap provider results.
 */
export type ProviderCoverageStatus =
  | 'success'
  | 'cached'
  | 'failed'
  | 'skipped'
  | 'unavailable'
  | 'baseline'
  /**
   * I (2026-08-12) — the provider RAN and completed but the site genuinely
   * has no data (a rural site with no local news / open-data portal). A
   * normal outcome, NON-blocking: it must produce neither a dispatch block
   * nor an audit-ledger warning — unlike 'unavailable' (fetch failed) or
   * 'missing' (never attempted, actionable via re-run).
   */
  | 'empty'
  | 'missing';

/**
 * Document Matching Quality DM4 — per-link evidence carried on each
 * EvidenceSection. Mirrors `RankedDocMatch` in
 * `src/config/documentChecklists.ts` (same shape, different
 * provenance — sections come from `buildSectionLinksRanked`, checklist
 * entries from `matchUploadedDocsToChecklistRanked`). Renderers (DM6,
 * e.g. `DocLineageInspector`) show the rationale + evidence quotes.
 */
export interface LinkedDocumentRef {
  docId: string;
  role: 'primary' | 'supporting';
  /** 0..1 from the DM2 scorer. */
  score: number;
  /** ≤200 chars from the DM2 scorer. */
  rationale: string;
  /** ≤3 short quotes from the DM2 scorer. */
  evidenceQuotes: readonly string[];
  /**
   * P4 — who stated the linked facts, in what role, when, and for which
   * project, as the document itself says. Rendered beside the linked-doc
   * rationale in the SED prompt so the report attributes the figure.
   */
  attribution?: EvidenceAttribution;
  /** True when the scorer read from the Firestore cache. */
  cached?: boolean;
  /**
   * Multi-Parcel Site Boundary (MP pack) — Phase 2 cross-pack scaffold.
   *
   * Optional id of the parcel this document link is scoped to.
   * References `SiteEvidenceDocument.parcels[].id` (MP4). When set, the
   * linked document supports the section's data for a single parcel
   * only (e.g. a Phase-1 land survey that covers the main parcel but
   * not the adjacent substation parcel).
   *
   * Absent = link applies to every parcel / site-wide (legacy
   * single-parcel SED, or a doc whose coverage isn't parcel-specific).
   * No runtime logic is wired against this field yet — DM6 renderers
   * gain parcel-aware "Primary - <fileName> (<parcel>)" framing in MP
   * Phase 3.
   */
  parcelId?: string;
  /**
   * REF-LINEAGE REF-2 — 1-based page in the source document the
   * linked claims were extracted from. Populated by the doc-aware
   * extractor (`extractFromDocument`, REF-1) when the doc carries
   * page structure (PDF, paginated DOCX). Drives the PDF viewer's
   * `initialPage` prop when a `[Ref N]` pill is clicked (REF-3).
   *
   * Optional for back-compat: SEDs persisted before REF-LINEAGE (every
   * SED currently in GCS) have no page; the chip + RefLink degrade to
   * today's filename-only behaviour without throwing. Readers that
   * deep-link MUST tolerate `pageNumber === undefined` — the
   * `resolveDocumentLineage` helper (REF-3) filters those out so the
   * scroll-to-appendix fallback fires.
   */
  pageNumber?: number;
  /**
   * Part C SP4 S5 — character span within the source document
   * (`SiteDocument.textContent`) the linked claims were extracted from.
   * Finer-grained than `pageNumber`: lets a future deep-link scroll to
   * the exact passage rather than the top of a page. The ref is already
   * scoped to one doc via `docId`, so `PassageOffset.docId` is omitted.
   *
   * Optional / additive — every SED currently in GCS has none, and the
   * deep-link resolver (`resolveCitationDeepLink`, S5) falls back to
   * page → filename when absent. No producer populates this yet.
   */
  sourceOffset?: import('./siteContext').PassageOffset;
  /**
   * REF-LINEAGE REF-2 — most-specific heading enclosing the
   * extracted passage (≤120 chars, enforced at projection time in
   * REF-1). Cosmetic only — surfaced on the appendix back-ref
   * preview (REF-4) and the chip tooltip. Optional for the same
   * back-compat reasons as `pageNumber`.
   */
  sectionHeading?: string;
  /**
   * REF-LINEAGE REF-2 — verbatim excerpt (≤140 chars, enforced at
   * projection time in REF-1) the extractor pulled out of the doc
   * to derive the linked claims. Renders as an italic snippet under
   * the appendix row (REF-4) and in the RefLink hover tooltip.
   * Optional.
   */
  excerptPreview?: string;
}

/** Bounded structured values accepted from validated document extraction. */
export type StructuredEvidenceValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[];

/**
 * Audited source rank for SED facts.  The rank is deliberately independent
 * of `confidence`: a high-confidence regional model cannot displace a
 * site-specific professional document.
 */
export type EvidenceSourceGrade =
  | 'verified-professional'
  | 'authoritative-government'
  | 'reviewed-landgate'
  | 'regional-screening'
  | 'inference';

/** Exact qualification used instead of turning missing/screening data into a false negative. */
export type EvidenceQualification =
  | 'verified'
  | 'screening'
  | 'estimated'
  | 'unknown'
  | 'unavailable';

export type TopographicDerivationMethod =
  | 'field-topographic-survey'
  | 'alta-table-a-item-5-contours'
  | 'lidar-tin-grade'
  | 'civil-grading-survey';

/**
 * Explicit structured topographic measurement/derivation support for parcel slope.
 * Document titles, filenames, or document types ('ALTA', 'geotechnical', 'topographic survey')
 * never establish numerical slope support without this structured record.
 */
export interface TopographicSurveySupportV1 {
  /** Finite measured parcel slope percentage established by the survey. */
  measuredSlopePct: number;
  /** Whether verified parcel topographic contours (e.g. ALTA Table A Item 5) are included. */
  topographicContours?: boolean;
  /** Positive contour interval in feet when contour-based. */
  contourIntervalFt?: number;
  /** Positive contour interval in meters when contour-based. */
  contourIntervalM?: number;
  /** Number of surveyed parcel elevation points sampled (>= 2 for point-derived survey grade). */
  surveyPointsSampled?: number;
  /** Explicit survey derivation method when derived from multi-point parcel survey/LiDAR/grading. */
  derivationMethod?: TopographicDerivationMethod;
  /** Optional explicit siteId binding for the survey measurement record. */
  siteId?: string;
}

/**
 * Fact-path provenance for composite sections (for example, public SSURGO
 * screening plus a professional geotechnical report).  This prevents a
 * section-level label from silently upgrading every nested fact.
 */
export interface EvidenceSourceContribution {
  /** Explicit synthetic attribution cannot acquire professional or government authority. */
  demonstrationDisclosure?: import('./demonstration').DemonstrationDisclosureV1;
  /** Dot path relative to `EvidenceSection.data`; `*` means the whole value. */
  path: string;
  grade: EvidenceSourceGrade;
  qualification: EvidenceQualification;
  source: string;
  sourceUrl?: string;
  retrievedAt: string;
  dataAsOf?: string;
  documentId?: string;
  pageNumber?: number;
  sectionHeading?: string;
  excerptPreview?: string;
  reviewState?: 'pending-review' | 'reviewed' | 'accepted' | 'rejected' | 'unavailable' | (string & {});
  /** Optional explicit status when distinct from qualification ('verified' | 'verified-from-doc' | ...). */
  status?: string;
  /** Optional explicit availability / state / coverage status flags. */
  available?: boolean;
  missing?: boolean;
  state?: string;
  coverageStatus?: string;
  /** Optional descriptive / caveat / excerpt fields inspected by fail-closed negation & proxy guards. */
  notes?: string;
  summary?: string;
  description?: string;
  excerpt?: string;
  sourceQuote?: string;
  rationale?: string;
  evidenceQuotes?: string[];
  /** Optional explicit siteId scope for the source contribution. */
  siteId?: string;
  /** Optional structured topographic slope measurement/derivation support. */
  topographicSupport?: TopographicSurveySupportV1;
  /** P4 — stated provenance of the contributing document passage. */
  attribution?: EvidenceAttribution;
}

/**
 * P4 — who stated a fact, in what role, on what date, and for which project.
 * Carried on linked document refs, source contributions and doc-vs-doc
 * conflicts so a figure prepared for a different project is never presented
 * as this site's. Every member optional; absent ⇒ the document did not say.
 */
export interface EvidenceAttribution {
  statedBy?: string;
  statedByRole?: string;
  organisation?: string;
  /** Date as written in the document. */
  statedOn?: string;
  appliesToProject?: 'this-site' | 'other-project' | 'other-site' | 'unknown' | (string & {});
  projectName?: string;
}

/** One document's value in a doc-vs-doc conflict, with the provenance it came with. */
export interface DocEvidenceConflictValue {
  value: string | number | boolean;
  docId: string;
  docName: string;
  attribution?: EvidenceAttribution;
  pageNumber?: number;
  excerptPreview?: string;
}

/**
 * P4 — competing values between two uploaded DOCUMENTS for the same field.
 * Distinct from `EvidenceConflict` (provider vs document): nothing here is
 * resolved by arrival order; the report must present every value with its
 * source. Dotted `field` paths address nested payloads
 * (`municipalSupply.avgMgd`).
 */
export interface DocEvidenceConflict {
  field: string;
  status: 'unresolved';
  detectedAt: string;
  values: DocEvidenceConflictValue[];
  /** Why the values may legitimately differ (another project, different dates). */
  note?: string;
}

/** Source statements retained for context; never adopted as current-site evidence. */
export interface DocSourceStatement {
  docId: string;
  docName: string;
  data: Record<string, unknown>;
  reason: 'other-project' | 'requested-load' | 'unverified-site-connection';
  attribution?: EvidenceAttribution;
  excerptPreview?: string;
  pageNumber?: number;
  sourceOffset?: { start: number; end: number };
}

/** The source of one adopted leaf, including the exact value before later human edits. */
export interface DocFieldSource {
  /** Exact machine-owned grid receipt copy; current=false withholds gas authority. */
  utilityPipelineProjection?: { schemaVersion: 1; current: boolean };
  demonstrationDisclosure?: import('./demonstration').DemonstrationDisclosureV1;
  docId: string;
  docName: string;
  value: string | number | boolean;
  confidence: number;
  attribution?: EvidenceAttribution;
  excerptPreview?: string;
  pageNumber?: number;
  sourceOffset?: { start: number; end: number };
}

/** Unresolved competing professional values; no last-write-wins resolution is implied. */
export interface ProfessionalEvidenceConflict {
  field: string;
  scope: 'geotechnical' | 'survey' | 'mineral-title' | 'archaeological-survey';
  status: 'unresolved';
  competingValues: Array<{
    value: StructuredEvidenceValue;
    documentId: string;
    documentName?: string;
    documentDate?: string;
    extractedAt: string;
    reviewState: 'pending-review' | 'reviewed' | 'accepted' | 'rejected';
  }>;
}

/**
 * Fresh-rebuild carry-forward provenance. A fresh bootstrap only contains
 * what providers returned THAT run; when a provider fails, the rebuild
 * folds the prior SED's last-good section back in and stamps it with this
 * marker. `retrievedAt`/`dataAsOf` keep the PRIOR values, so staleness
 * surfaces flag the section honestly — this stamp only records that the
 * value survived a rebuild rather than being re-fetched.
 */
export interface SedCarryForwardStamp {
  /** When the rebuild carried the section forward (ISO). */
  at: string;
  reason: 'absent-on-rebuild' | 'unavailable-on-rebuild';
}

/** Original EIA aggregate kept separate when another source adopts the price. */
export interface ElectricityPriceContext {
  retailRate?: number | null;
  state?: string;
  year?: number;
  basis?: 'state-industrial-average';
  sector?: 'IND';
  units?: 'cents per kilowatt-hour';
}

export interface EvidenceSection<T> {
  /** Present on document-only synthetic fallbacks; mixed provider leaves keep their own source rank. */
  demonstrationDisclosure?: import('./demonstration').DemonstrationDisclosureV1;
  status: EvidenceStatus;
  data: T | null;
  source: string;
  sourceUrl?: string;
  /** Present ONLY on sections a fresh rebuild carried from the prior SED. */
  carriedForward?: SedCarryForwardStamp;
  retrievedAt: string;
  /** Optional on legacy SEDs; use the compatibility resolver when absent. */
  sourceGrade?: EvidenceSourceGrade;
  /** Optional on legacy SEDs; never infer a negative from an absent value. */
  qualification?: EvidenceQualification;
  /** Per-fact source snapshots for sections containing multiple evidence grades. */
  sourceContributions?: EvidenceSourceContribution[];
  /**
   * P4 — competing values between uploaded documents for fields of this
   * section. Preserved, never resolved by arrival order; `sedToText`
   * renders every value with its source and attribution. Additive —
   * absent on every SED persisted before P4.
   */
  docConflicts?: DocEvidenceConflict[];
  docSourceStatements?: DocSourceStatement[];
  /** Dot paths relative to data. Provider leaves never inherit a nearby document's source. */
  docFieldSources?: Record<string, DocFieldSource>;
  /**
   * SED D3.1 — source VINTAGE (as-of-when-TRUE date of the underlying
   * datum), distinct from `retrievedAt` (the time WE fetched it). The SED
   * historically conflated the two: a gas-infrastructure proximity figure
   * whose underlying dataset is from 2021-02 would show today's fetch
   * timestamp and read as "fresh" when the real-world datum is years old.
   *
   * Producers stamp `dataAsOf` ONLY where the source exposes a genuine
   * effective/period date (FEMA NFHL effective date, EIA series period,
   * `GasInfraProximity.dataVintage`, NOAA normals epoch `1991-2020`, PHMSA
   * `reportYear`). When the source has no effective date the producer
   * leaves `dataAsOf` undefined and (optionally) sets
   * `dataAsOfBasis: 'unknown'`.
   *
   * Readers default `dataAsOf ?? retrievedAt`. The staleness classifier
   * keeps keying off `retrievedAt` (freshness of OUR fetch) — `dataAsOf`
   * is a DISPLAY signal surfaced only when it diverges materially from
   * `retrievedAt`. NO scoring change: this field is never read by the
   * scorer / completeness math. Optional + additive — every legacy SED
   * reads `dataAsOf === undefined` (renderers fall through to today's
   * `retrievedAt` behaviour).
   */
  dataAsOf?: string;
  /**
   * SED D3.1 — provenance of `dataAsOf`. Tells the reader HOW the vintage
   * was established so a coarse "publication"-derived date isn't presented
   * with false precision:
   *  - `'source-effective-date'` — the source carries a hard effective
   *    date (FEMA NFHL panel effective date, a permit issue date).
   *  - `'source-period'`         — the datum is a period aggregate (EIA
   *    monthly series period, NOAA 1991-2020 climate normals).
   *  - `'publication'`           — only a publication/release date is
   *    known, not the as-of date of the underlying observation.
   *  - `'fetch-time-fallback'`   — no source date; `dataAsOf` (if set at
   *    all) equals `retrievedAt`.
   *  - `'unknown'`               — the source exposes no vintage signal.
   *
   * Optional — absent ⇒ unknown / not recorded.
   */
  dataAsOfBasis?:
    | 'source-effective-date'
    | 'source-period'
    | 'publication'
    | 'fetch-time-fallback'
    | 'unknown';
  /**
   * SED D3.2 — value-level numeric UNCERTAINTY / error-bar, distinct from
   * `confidence` (which is provider RELIABILITY, 0-1) and the EAS
   * `attemptStages` (sampling audit). One entry per numeric field that
   * carries a known spread:
   *  - `field`         — the field key on `data` the band applies to
   *                      (e.g. `'elevationM'`, `'waterWithdrawal'`).
   *  - `pointEstimate` — the central value (mirrors `data[field]`).
   *  - `low` / `high`  — optional bounds of the interval.
   *  - `ciBasis`       — how the band was derived (`'provider-reported'`,
   *                      `'sampling-spread'`, `'class-estimate'`,
   *                      `'model'`).
   *  - `relativeWidthPct` — optional `(high-low)/|pointEstimate|` × 100,
   *                      pre-computed for the renderer + the scoring path.
   *
   * HARD RULE: never fabricate a band. A field with no genuine spread
   * carries NO entry, and an absent `valueUncertainty` contributes 0 to
   * scoring. NaN/Infinity bounds are scrubbed at write time (no non-finite
   * value is ever persisted). Optional + additive — legacy SEDs read as
   * `undefined`.
   *
   * SCORING: when (and only when) the default-OFF
   * `PORTFOLIO_CONFIG.ENABLE_D3_UNCERTAINTY_SCORING` flag is flipped on,
   * wide bands feed a small capped data-confidence penalty
   * (`computeDataConfidencePenalty` `wideUncertainty` term). Until then
   * this is pure telemetry — the composite/confidenceTag/decisionBand are
   * byte-identical.
   */
  valueUncertainty?: Array<{
    field: string;
    pointEstimate: number;
    low?: number;
    high?: number;
    ciBasis: 'provider-reported' | 'sampling-spread' | 'class-estimate' | 'model';
    relativeWidthPct?: number;
  }>;
  confidence: number;       // 0-1, based on provider reliability and data recency
  anomalies: string[];      // cross-validation flags
  /**
   * Reviewed commercial screening facts that may inform narrative but never
   * overwrite this section's authoritative/provider data.
   */
  reviewedScreeningEvidence?: Array<{
    providerId: 'landgate';
    sourceGrade: 'reviewed-landgate';
    qualification: 'screening';
    /** Reviewed commercial screening is never an authoritative report citation. */
    citationEligible: false;
    /** Historic review events are client-recorded until server promotion is available. */
    provenanceAuthority: 'client-recorded-review';
    /** Stable event and content identities for audit/diff surfaces. */
    reviewEventId: string;
    reviewContentHash: string;
    layerId: string;
    featureId?: string;
    sourceUrl?: string;
    retrievedAt: string;
    dataAsOf?: string;
    reviewedAt: string;
    reviewedBy: { uid: string; email?: string };
    facts: import('./site').LandGatePromotedFact[];
  }>;
  rawResponseHash?: string; // SHA-256 of raw API response for audit
  /**
   * Library + user documents linked to this section.
   *
   * Document Matching Quality DM4 — back-compat projection of
   * `linkedDocumentRefs.map(r => r.docId)`. New code MUST write to
   * `linkedDocumentRefs` (carries role + rationale + score). Existing
   * readers (the SED text formatter, the report prompt assembly)
   * continue to read this
   * derived field and Just Work — DM6 is the surface-side
   * incremental migration to the ranked refs.
   */
  linkedDocumentIds?: string[];
  /**
   * Document Matching Quality DM4 — content-aware ranked links per
   * SED section. Populated by the DM4 path inside `sedBootstrap`.
   * Carries the DM2 scorer's rationale + evidenceQuotes so
   * renderers (DM6) can surface "Primary - <fileName> -
   * '<rationale>'" without re-scoring. Optional for back-compat: a
   * SED written before DM4 reads as the legacy id-list shape via
   * `linkedDocumentIds`.
   */
  linkedDocumentRefs?: LinkedDocumentRef[];

  /**
   * Documents_On_Site Prompt 11 — doc-aware confidence layer.
   *
   * `supportingDocIds` lists the docs the section was derived from
   * (when status = 'verified-from-doc') OR the docs that boosted an
   * already-verified provider section. `docContributionScore` is a
   * 0..1 signal of how much the doc influenced the result; for
   * fallback extractions it's effectively 1.0, for boosts it's the
   * relevance score of the strongest matching doc.
   *
   * `adjustedConfidence` overrides `confidence` for downstream display
   * + AI hedging when the doc layer participated. Renderers and the
   * confidence-aware language helper read `adjustedConfidence ??
   * confidence` so legacy SEDs keep working.
   */
  supportingDocIds?: string[];
  docContributionScore?: number;
  adjustedConfidence?: number;

  /**
   * Site Score Movement SSM-1 — provenance of `slot.data` after the
   * `mergeProviderAndDocEvidence` resolver runs. Distinguishes the four
   * source-paths so the scorer (SSM-3) and the Score Provenance Panel
   * (SSM-6) can branch on it:
   *
   *  - `'provider'`      — provider data only. Status quo. Default.
   *  - `'doc'`           — provider section was unavailable; doc
   *                        extraction filled the slot. Equivalent to
   *                        the legacy `'verified-from-doc'` branch.
   *  - `'provider+doc'`  — both present; values agree (confidence
   *                        boost) OR conflict (flagged via
   *                        `evidenceConflicts`, provider preferred
   *                        until resolved).
   *  - `'doc-override'`  — doc carries authoritative ground-truth for
   *                        this section (e.g. ISO Queue Letter for
   *                        `grid.capacity`); doc value replaces
   *                        provider value.
   *
   * Optional for back-compat — SEDs written before SSM read as
   * `undefined` (the renderer + scorer treat that as `'provider'`).
   *
   * RDX-1 widens the union with three report-aware members. They are
   * stamped by the post-`generateTypedReport` ingestion hook
   * (`applyReportToSed`) when a prior report concluded a value the SED
   * resolver chooses to adopt:
   *
   *  - `'report-derived'`           — a prior report's finding fills the
   *                                   slot. Equivalent to the `'doc'`
   *                                   tier shape, but the source is a
   *                                   generated report rather than an
   *                                   uploaded document.
   *  - `'report+provider'`          — both present, provider retained
   *                                   but the report's finding agrees /
   *                                   contributes confidence (mirror of
   *                                   `'provider+doc'`).
   *  - `'report-overrides-provider'`— the report's finding is treated
   *                                   as authoritative for this slot
   *                                   (e.g. Feasibility nailed an exact
   *                                   capacity the provider only had as
   *                                   a bucket). Wave-2 resolver gate.
   *
   * Wave-1 only writes the new members from `applyReportToSed` — no
   * resolver / consumer surface reads them yet. UI fall-through to
   * `'provider'` is the safe default for switch statements that
   * pre-date RDX.
   *
   * CAR-6 widens the union with a SINGLE `'auto-fill'` member. The
   * specific provider id (`'nrcs-soil-survey'`, `'fema-nfhl'`,
   * `'hifld-grid'`, `'regrid-title'`, `'county-zoning'`, ...) is stamped
   * on `provenanceDetail.autoFillProvider` so the "auto-fill:<provider>"
   * shape the pack §CAR-6 spec describes is materialised as the pair
   * `provenance: 'auto-fill' + provenanceDetail.autoFillProvider`. This
   * keeps the union finite (TypeScript can't ergonomically express
   * `auto-fill:${string}` template literals across switch/case sites)
   * while preserving the per-provider attribution the Score Provenance
   * Panel needs. Auto-fill ranks BELOW user-doc / authored / report /
   * provider tiers — the resolver only stamps it when the slot would
   * otherwise be `unavailable`.
   */
  provenance?:
    | 'provider'
    | 'doc'
    | 'provider+doc'
    | 'doc-override'
    | 'report-derived'
    | 'report+provider'
    | 'report-overrides-provider'
    | 'auto-fill';
  /**
   * SSM-1 — structured detail for `provenance`. Carries the original
   * provider value and the doc-derived value so the Score Provenance
   * Panel (SSM-6) can render a per-category breakdown without the
   * caller having to re-derive it. `mergeReason` is a short
   * stable code (`'authoritative-override'`, `'agreement-boost'`,
   * `'conflict-pending-review'`, `'doc-fills-gap'`, `'provider-only'`,
   * `'report-fills-gap'`, `'report-agreement'`, `'report-override'`)
   * the panel can also key off for copy variation. Optional for the
   * same back-compat reasons as `provenance`.
   *
   * RDX-1 adds the `derivedFromReport` channel. Populated when the
   * slot's value came from a prior report; carries the originating
   * `reportId` / `reportType` + `reportVersion` so the resolver and the
   * UI can attribute the value back to the source report. Optional
   * (absent on every legacy slot).
   */
  provenanceDetail?: {
    provider?: string;
    docId?: string;
    mergeReason: string;
    providerValue?: unknown;
    /** Context belongs to the prior aggregate, never the adopted scalar. */
    electricityPriceContext?: ElectricityPriceContext;
    docValue?: unknown;
    /** P4 — stated provenance of the document passage that filled the gap. */
    attribution?: EvidenceAttribution;
    /**
     * CAR-6 — the specific auto-fill provider id when the slot's
     * effective value came from a CAR provider chain (`'nrcs-soil-survey'`,
     * `'fema-nfhl'`, `'hifld-grid'`, `'regrid-title'`, `'county-zoning'`,
     * ...). Always paired with `provenance === 'auto-fill'`. The pair
     * encodes the pack §CAR-6 `"auto-fill:<provider>"` shape without
     * blowing up the finite `provenance` union with one literal per
     * provider. Multiple providers contributing to the same slot are
     * recorded under `autoFillProviders` (plural) for the agreement
     * boost / disagreement flag paths. Optional — back-compat with
     * every legacy slot (absent ⇒ no auto-fill applied).
     */
    autoFillProvider?: string;
    /**
     * CAR-6 — when MULTIPLE auto-fill providers contributed to the same
     * slot (e.g. NRCS + USGS both attesting a groundwater depth), the
     * full ordered list lives here while `autoFillProvider` carries the
     * winning (first / highest-confidence) entry. Used by the agreement
     * boost path (≥2 providers in agreement → confidence + 0.05) and
     * the disagreement-flag path (≥2 providers in disagreement → emit
     * `evidenceConflicts` row with `providerSource: 'auto-fill:multi'`).
     * Absent when only one auto-fill provider contributed.
     */
    autoFillProviders?: string[];
    /**
     * CAR-6 — per-field structured payload the auto-fill compiler
     * produced for this section (mirrors the `RequirementAutoFill.fields`
     * the AuthorEvidenceDialog prefill consumes). Carried so the Score
     * Provenance Panel can surface "Auto-filled fields: floodZone=AE,
     * topOfLowestFloor_ft=249.8" without re-running the compiler.
     * `Number.isFinite()` is enforced at write time for every numeric
     * value; non-finite values are dropped (HARD RULE — no NaN /
     * Infinity in persisted SED). Absent when the auto-fill compiler
     * produced no structured fields (e.g. zero-fill regulatory path
     * where only the provenance stamp was useful).
     */
    autoFillFields?: Record<string, StructuredEvidenceValue>;
    /** Each populated field keeps its actual source when several documents contribute. */
    autoFillFieldSources?: Record<string, Omit<DocFieldSource, 'value'> & {
      value: StructuredEvidenceValue;
      requirementId: string;
    }>;
    /**
     * Per-field human decisions about extracted values, keyed by the same leaf
     * key as `autoFillFields`.
     *
     * Deliberately METADATA rather than a `status` member. `status` drives
     * ranking, matrix colour, readiness and report eligibility, so adding a
     * value there means auditing every consumer — and it would overstate the
     * claim. Accepting an extraction means "I checked this matches the
     * document"; it does not make the document a better source. This mirrors
     * the LandGate review precedent above, where a client-recorded review is
     * explicitly `citationEligible: false`.
     *
     * A `rejected` decision DELETES the value from `autoFillFields` and keeps
     * it in `previousValue`, so the removal is reversible. Leaving a value a
     * human has called wrong would be worse than never extracting it, because
     * everything downstream reads a present value as evidence.
     */
    fieldReviews?: Record<string, {
      decision: 'accepted' | 'corrected' | 'rejected';
      reviewedAt: string;
      reviewedBy: string;
      previousValue?: StructuredEvidenceValue;
    }>;
    /**
     * D5 observability — schema keys the last stamp pass extracted but then
     * DROPPED under the populate-source policy (tier not allowed for the
     * key) or the professional-field filter. Before this existed those drops
     * were silent: the extractor did the work, the filter ate it, and
     * nothing anywhere said so. Sorted, capped, REPLACED on every stamp
     * (it describes the most recent pass, not history). Review-suppressed
     * keys are deliberately NOT recorded here — those are visible human
     * state in `fieldReviews`, not silent loss.
     */
    autoFillDropped?: string[];
    /**
     * RDX-1 — when the slot's effective value was sourced from a prior
     * generated report on this site, this object describes that source.
     * `reportVersion` participates in the idempotency key
     * `${reportId}__${sedFieldPath}` so a rerun of the same report
     * version is a no-op while a NEW version supersedes prior claims.
     */
    derivedFromReport?: {
      reportId: string;
      reportType: import('../config/reportTypes').ReportTypeId;
      reportVersion: number;
      sectionId: string;
      generatedAt: number;
      verdict?: string;
    };
    /**
     * DCO-1 — populated when the slot's effective value came from a
     * per-slot grounded AI Research call (`dispatchResearch`). Carries
     * the source URL/title (clickable in the UI), the retrieval time,
     * the model name + query text (auditable), the model's confidence,
     * and the raw grounding-chunk metadata (optional; large, so
     * stripped from the SED-text projection but kept on the structured
     * record for the lineage inspector).
     *
     * Constitutional invariant: any slot with
     * `status === 'verified-from-research'` MUST have a non-empty
     * `researchSource.sourceUrl` — the orchestrator rejects research
     * results without a citation. Tests pin this; UI helpers assume
     * the URL is present when the status fires.
     */
    researchSource?: {
      sourceUrl: string;
      sourceTitle: string;
      retrievedAt: number;
      queryText: string;
      modelName: string;
      confidence: number;
      /** Raw grounding-chunk array from the @google/genai response — kept
       *  on the structured record for the EVL lineage inspector. May be
       *  empty when the SDK didn't surface chunks even though the primary
       *  URL was filled (defensive). */
      groundingChunks?: Array<{ uri: string; title?: string }>;
      /** Short verbatim quote from the cited source supporting the
       *  answer. Capped at 200 chars by the dispatcher. Optional — some
       *  grounded answers don't return a quote. */
      supportingQuote?: string;
    };
    /**
     * DCO-1 (Wave B placeholder) — populated when the slot's value came
     * from a curated DAM entry. Wave A does not write this; the field
     * is declared so consumers can be widened in lockstep without a
     * second schema-change commit when Wave B lands.
     */
    damSource?: {
      damVersion: string;
      damEntryId: string;
      retrievedAt: number;
      confidence: number;
    };
    /**
     * DCO-1 — when the orchestrator falls through to `'unavailable'`
     * after trying every tier, this carries the ordered list of tiers
     * that were attempted (e.g. `['provider', 'doc', 'research']`) so
     * the lineage inspector can render "tried X, Y, Z — no source".
     * Optional and best-effort; absent when DCO didn't run.
     */
    attemptedTiers?: Array<'provider' | 'doc' | 'authored' | 'dam' | 'research'>;
    /** DCO-1 — human-readable reason carried with `attemptedTiers` when
     *  the orchestrator declared the slot unavailable. */
    unavailableReason?: string;
    /**
     * DR-2 (DCO-FOLLOWUP-MULTI-PROVIDER-CONSENSUS) — quality warnings
     * surfaced by the orchestrator alongside the slot's filled value.
     * The first defined warning type is `'dco-research-conflict'`,
     * stamped when the research-tier dispatcher detected ≥2 grounding
     * chunks with disagreeing values. The slot's PRIMARY value still
     * lives in `slot.data`; this entry records that other equally-
     * grounded sources disagreed so the section editor + lineage
     * inspector can render a "🔍 Sources disagree" chip and surface
     * the alternates for an analyst to resolve.
     *
     * Optional and additive — back-compat with every existing SED
     * (`qualityWarnings === undefined` reads as "no conflicts").
     */
    qualityWarnings?: Array<{
      type: 'dco-research-conflict';
      sectionId: string;
      fieldKey?: string;
      primaryValue: unknown;
      alternateValues: unknown[];
      alternateUris: string[];
      distinctValueCount: number;
    }>;
  };

  /**
   * Site Coordinate Accuracy CA6 — the coordinate this section's provider was
   * actually queried at, with provenance:
   *  - `centroid`        — the boundary centroid (`centroidOf(siteBoundary)`),
   *    used by the spatially-varying providers when a polygon is drawn.
   *  - `address`         — the geocoded-address point (Google Places lookup of
   *    the typed address). Used by providers whose domain is coarse enough not
   *    to need the centroid, and by every provider on a legacy site with no
   *    drawn boundary.
   *  - `boundary-sample` — the provider sampled multiple points across the
   *    parcel boundary (the FEMA flood sweep, the USFWS buffered envelope);
   *    the recorded lat/lng is the representative point (centroid).
   * Optional so legacy SEDs (and sections compiled without the coordinate
   * context) keep working — absent ⇒ provenance unknown / not recorded.
   */
  queryPoint?: { lat: number; lng: number; source: 'centroid' | 'address' | 'boundary-sample' };

  /**
   * UAE-1 — ids of the user-authored `ExtractedClaim` entries that
   * fed this section's data. Populated when
   * `applyDocAwareConfidence` (or SSM's future `mergeAllEvidence`)
   * slotted an authored claim into an otherwise-`unavailable` section
   * and stamped `status: 'verified-from-context'`. Renderers surface
   * this as the authored provenance trail (alongside `supportingDocIds`
   * for doc-fallback evidence). Optional for back-compat with legacy
   * SEDs.
   */
  authoredClaimIds?: string[];

  // ──────────────────────────────────────────────────────────────────
  //  PRE — Provider Evidence Honesty (row 0.99251)
  //
  //  Customer-trust hotfix (2026-05-18): legacy code emitted
  //  `status: 'verified'` + `source: 'Google Maps Elevation API'` for
  //  the `siteProfile` section even when EVERY elevation value inside
  //  the section was null (Google API returned `Data unavailable`).
  //  The renderer then surfaced "—m · User Drawn Boundary (Google Maps
  //  Drawing + Elevation API)" which reads as "the platform is broken"
  //  when the truth is "the provider returned no data".
  //
  //  These fields let the producer record, and the renderer surface,
  //  the honest "attempted but empty" state without losing the
  //  attribution. Composes with PU4's `ProviderFailureMode`: PU4 is
  //  the dispatcher-level fail-mode (network, auth, etc.); PRE is the
  //  per-section "provider was attempted, returned no usable data"
  //  state — orthogonal to the dispatcher classification.
  //
  //  Three honest states beat one misleading state:
  //    - value present + source        → confident chip (existing)
  //    - value empty + attemptedProvider → "⚠ Provider — reason" chip
  //    - value empty + no attempt        → "— not collected" chip
  //
  //  All fields are optional for back-compat: every existing SED
  //  reads as `attemptedProvider: undefined` (no chip change).
  // ──────────────────────────────────────────────────────────────────

  /**
   * PRE-1 — when the section's `status` would otherwise be `'verified'`
   * (or any verified-tier status) but every meaningful value in
   * `data` is null/empty, the producer stamps `status: 'unavailable'`
   * AND records the originating provider id here. The renderer reads
   * this as "we DID attempt this provider — it just had no data" and
   * surfaces an honest empty-with-attribution chip instead of either
   * a fabricated success or a silent gap.
   *
   * Distinct from the section's plain `source` string: `source` is
   * the human-readable label for a successful claim; `attemptedProvider`
   * is the canonical id of the provider that was attempted. Both can
   * coexist (label stays for the appendix; id drives the empty-chip
   * vocabulary).
   */
  attemptedProvider?: string;

  /**
   * PRE-1 — what the provider returned. Mirrors and complements PU4's
   * dispatcher-level `ProviderFailureMode`:
   *  - `success`        — provider returned usable data (default for
   *                       verified-tier sections; rarely written
   *                       explicitly).
   *  - `empty`          — provider responded with no data for this
   *                       parcel (HTTP 200 + empty results, polygon
   *                       outside coverage, etc.). The honest "we
   *                       asked, nothing came back" state.
   *  - `http-error`     — non-200 HTTP response (4xx/5xx).
   *  - `timeout`        — request timed out before a response.
   *  - `rate-limited`   — provider returned 429 / rate-limit signal.
   *  - `not-attempted`  — provider was skipped at dispatch (coverage
   *                       gating, missing API key, etc.).
   *
   * Optional for back-compat. Producer writes the field together with
   * `attemptedProvider`; renderer reads both as a pair.
   */
  attemptResult?:
    | 'success'
    | 'empty'
    | 'http-error'
    | 'timeout'
    | 'rate-limited'
    | 'not-attempted';

  /**
   * PRE-1 — ISO timestamp of the most recent attempt. Lets renderers
   * surface "Last attempted 6d ago" without re-fetching. Distinct from
   * `retrievedAt`: that field carries the timestamp of the last
   * SUCCESSFUL retrieval; `lastAttemptedAt` is set on every attempt
   * (success or failure). Optional.
   */
  lastAttemptedAt?: string;

  /**
   * PRE-1 — ISO timestamp of the next scheduled auto-retry. Computed
   * by `nextRetryFromBackoff(attemptResult, attempt)` at the producer
   * boundary; renderers surface "Retrying in 2h" so users know whether
   * to wait or amend manually. Optional — `undefined` means no
   * auto-retry is scheduled (legitimate empty after backoff exhausted,
   * or coverage gate that won't resolve on retry).
   */
  nextRetryAt?: string;

  /**
   * PRE-1 — human-readable reason the section is empty. Surfaced in
   * the renderer tooltip + chip text. Stable enough to read as
   * documentation ("Provider returned no elevation data for this
   * polygon (likely outside coverage or polygon too small).") but
   * cheap enough for the producer to write a one-liner per failure
   * mode. Optional.
   */
  emptyReason?: string;

  /**
   * SED D3.5 — WHY a section is empty, as a first-class discriminator.
   * Complements the PRE `attemptResult` (which classifies a PROVIDER
   * outcome) by distinguishing the cause of an `unavailable` slot so a
   * silently-empty intent-stamped section (BTM / technology / firm-
   * capacity sections that come back empty because `projectIntent` is
   * thin) is no longer indistinguishable from a failed provider:
   *  - `'provider-empty'`   — a provider was attempted and returned no
   *                           usable data (the PRE `attemptResult:'empty'`
   *                           case).
   *  - `'intent-missing'`   — the section is intent-stamped and the source
   *                           intent slot is empty (the user hasn't
   *                           declared cooling / power / firm-capacity yet).
   *                           Surfaces as a "needs intent input" gap, not a
   *                           silent blank.
   *  - `'non-us-coverage'`  — the section's provider was skipped because
   *                           the site is outside US coverage (mirrors the
   *                           dispatcher `coverageMode` reason).
   *  - `'not-attempted'`    — the section was never attempted (coverage
   *                           gate, disabled provider, etc.).
   *
   * Optional + additive — every legacy SED reads `unavailableCause ===
   * undefined`. NOT a new `EvidenceStatus` value: the status ladder + every
   * status switch stay untouched. NO scoring change.
   */
  unavailableCause?: 'provider-empty' | 'intent-missing' | 'non-us-coverage' | 'not-attempted';

  /**
   * MP Phase 3 — per-parcel provider outputs keyed by parcel id.
   * Only populated when the site has N >= 2 parcels. Single-parcel
   * sites leave this undefined; `data` carries the aggregate.
   */
  perParcel?: Record<string, T>;

  /**
   * MP Phase 3 — aggregate rollup metadata when per-parcel variance
   * is detected (worst vs mean tier differs by >= 1).
   */
  parcelAggregate?: {
    varianceFlagged: boolean;
    meanTier?: number;
    worstTier?: number;
    worstParcelId?: string;
  };

  // ──────────────────────────────────────────────────────────────────
  //  EAS — Elevation Averaging Sampling (row 0.99259)
  //
  //  When the section's value came from a multi-stage cascade — the
  //  EAS sampler tries polygon vertices → in-polygon grid →
  //  buffered-bbox grid → USGS centroid before giving up — record
  //  each stage's outcome so the chip tooltip can surface "Tried 4
  //  strategies: vertex 0/4 returned · in-polygon-grid 18/25 returned
  //  · stopped (sufficient samples)" without re-running the cascade.
  //
  //  Optional, additive: every existing SED reads as
  //  `attemptStages: undefined` and the renderer's old "Source:
  //  google-maps-elevation" path is unchanged.
  // ──────────────────────────────────────────────────────────────────

  /**
   * EAS-4 — per-stage audit trail from the cascading sampler. One
   * entry per stage actually executed (the cascade short-circuits on
   * first success, so downstream stages do not appear when an earlier
   * stage succeeded). Stamped on the section by the producer alongside
   * `source` / `attemptResult` / `emptyReason` and read by the PRE
   * chip tooltip.
   *
   * Shape mirrors `ElevationSamplingStageRecord` in
   * `services/dataProviders/elevation/cascadingSampler.ts`. We do NOT
   * import that type here — keeps the SED schema independent of
   * provider-specific code (the EvidenceSection interface is reused
   * across providers and shouldn't pull in the elevation cascade
   * module just to declare a property). The stage string union is
   * intentionally narrow enough to be the source of truth for
   * renderer copy + tests.
   */
  attemptStages?: ReadonlyArray<{
    stage:
      // RD-1 (C6) — Stage 0: self-hosted USGS 3DEP snapshot sample.
      | 'refdata-3dep-grid'
      | 'vertex'
      | 'in-polygon-grid'
      | 'buffered-bbox-grid'
      | 'usgs-epqs'
      // EX-2 (EAS-FOLLOWUP-NOAA-CLIMATE) — single-stage centroid
      // query for the NOAA NCEI Climate Normals auxiliary fetch.
      // Climate normals are regional; a 5×5 grid would return
      // identical values from the same station record, so the
      // attempt is "centroid" (one query, one record).
      | 'centroid';
    pointsRequested: number;
    pointsReturned: number;
    durationMs: number;
    error?: string;
  }>;

  // ──────────────────────────────────────────────────────────────────
  //  SED D0.3 — extraction-recall ("matched but not extracted")
  //
  //  Before D0.3 the doc-aware confidence layer dropped a matched doc's
  //  extraction silently in four places (unavailable-only gate, the
  //  0.4/0.6 floor mismatch, the hard `required` arrays, and three
  //  `return null` / delete-field paths). The user saw a "matched" doc
  //  with NO explanation of why nothing landed on the slot.
  //
  //  This OPTIONAL, additive side-field records that an extraction WAS
  //  attempted against a matched doc but produced no usable field for a
  //  still-empty slot, with a structured reason. It is deliberately a
  //  side-field — NOT a new `EvidenceStatus` union member — so the
  //  existing status ladder + every status switch (scorer, renderers,
  //  coverage gate, PDF/DOCX) stay untouched, while the Coverage Matrix
  //  chip/tooltip gains a non-silent "attempted & rejected: <reason>"
  //  surface. Back-compat: every persisted SED reads
  //  `extractionAttempt === undefined` (no behaviour change).
  //
  //  A slot carrying `extractionAttempt.attempted === true` whose data
  //  is still empty MUST NOT count as populated (see
  //  `requirementStatus.ts:isSlotPopulatedByDoc`).
  // ──────────────────────────────────────────────────────────────────
  extractionAttempt?: {
    /** True when the doc-aware layer ran (or short-circuited) an
     *  extraction against a matched doc for this slot. */
    attempted: boolean;
    /** Id of the matched doc the extraction was attempted against. */
    docId?: string;
    /**
     * Why the attempt yielded nothing usable. Mirrors
     * `ExtractionRejectionReason` in
     * `services/knowledge/extractFromDocument.ts` (NOT imported here —
     * keeps the SED schema free of a service-layer dependency). The
     * `'low-confidence'` reason is bootstrap-side (the doc matched the
     * checklist but fell below the extraction-attempt floor), distinct
     * from the extractor's own rejection reasons.
     */
    reason?:
      | 'all-fields-out-of-range'
      | 'cross-state'
      | 'all-required-missing'
      | 'call-failed'
      | 'empty-text'
      | 'binary'
      | 'no-schema'
      | 'low-confidence';
    /**
     * EX-2 breadcrumbs carried through from the extractor when the
     * rejection was caused by the range/locus validator deleting every
     * field. Shape mirrors `DocFieldRejection` (field, canonicalPath,
     * rejected, reasons) — inlined to avoid importing the service type.
     */
    fieldRejections?: Array<{
      field: string;
      canonicalPath: string;
      rejected: string | number | boolean;
      reasons: string[];
    }>;
    /** DAT-FOLLOWUP-1 breadcrumb carried through on a cross-state
     *  rejection. */
    crossStateRejection?: {
      field: 'state';
      rejected: string;
      siteState: string;
    };
    /** ISO timestamp of the attempt. */
    at: string;
  };
}

/** Cross-validation check result. */
export interface CrossValidationCheck {
  rule: string;
  passed: boolean;
  message: string;
  severity: 'error' | 'warning' | 'info';
  fields: string[];         // SED fields involved
}

/** Data gap with impact assessment. */
export interface DataGap {
  field: string;
  reason: string;
  impact: 'critical' | 'significant' | 'minor';
  recommendation: string;   // what the user should do to fill this gap
  /**
   * Canonical id of the provider whose missing data triggered the
   * gap (e.g. 'nrel-wind', 'eia', 'airnow'). Optional for back-compat
   * with legacy SEDs written before Content-Source Fixes Prompt 10;
   * when present, `classifyGap` matches against it exactly instead
   * of doing the substring scan of `reason`.
   */
  providerId?: string;
}

/** Expert amendment to SED data. */
export interface Amendment {
  field: string;
  originalValue: string | number | boolean | null;
  correctedValue: string | number | boolean | null;
  correctedBy: string;
  reason: string;
  source?: string;          // e.g. "site visit", "utility confirmation", "geotechnical report"
  timestamp: string;
}

/* ── SED-FOUNDATION — evidence workflow items ─────────────────────────
 *
 * Evidence-first foundation for the investment-grade report loop. Three
 * orthogonal workflow concerns are unified under one `EvidenceWorkflowItem`
 * shape carried on `SED.workflows` (all OPTIONAL — legacy SEDs without the
 * field behave exactly as today):
 *
 *   - `open-question`       — an unresolved verification question lifted from
 *                             a generated report's `FindingClaim` open-question
 *                             diction (R-131 epistemic grounding). WARN-level:
 *                             it lowers readiness/confidence but never blocks
 *                             report generation (USER DECISION — no depth-gate).
 *   - `verification-status` — a confirmed FindingClaim / verification that
 *                             MUST carry evidence (refIds + non-empty excerpt).
 *                             Missing evidence → WARNING folded into readiness.
 *   - `siting-note`         — a structured, versioned, auditable record of a
 *                             siting observation (see `SitingNoteEntry`). When
 *                             `verificationMethod === 'site-visit'` evidence +
 *                             notes are REQUIRED (validated at apply time).
 *
 * NO fabricated / placeholder data is ever written into these — a workflow
 * item only exists because a report, an operator, or a site visit produced it.
 */

/** Workflow item kind discriminator. */
export type EvidenceWorkflowKind =
  | 'open-question'
  | 'verification-status'
  | 'siting-note';

/**
 * How a verification / siting observation was established. Mirrors the
 * `AmendmentSource.type` vocabulary so the two systems read consistently.
 * A `'site-visit'` carries the strongest evidence requirement.
 */
export type WorkflowVerificationMethod =
  | 'document'
  | 'external-link'
  | 'expert-judgment'
  | 'extracted-context'
  | 'site-visit'
  | 'provider'
  | 'report';

/**
 * SED-FOUNDATION — a single structured siting note. Versioned + auditable:
 * entries are append-only and never overwritten (corrections create a new
 * entry, like amendments). When `verificationMethod === 'site-visit'`,
 * `linkedDocIds` (evidence) AND a non-empty `text` (notes) are REQUIRED —
 * `validateSitingNoteEntry` warns + refuses to mark the parent item verified
 * if either is missing.
 */
export interface SitingNoteEntry {
  /** Stable id (caller-supplied; e.g. `siting__<ts>__<rand>`). */
  id: string;
  /** ISO timestamp the note was authored. */
  date: string;
  /** Who authored it — UID, email, or a system actor label. */
  actor: string;
  /** Free-form observation text. Required + non-empty for a site-visit. */
  text: string;
  /** SiteDocument ids backing this note (photos, survey, geotech, KMZ). */
  linkedDocIds: string[];
  /** How the observation was established. */
  verificationMethod: WorkflowVerificationMethod;
}

/**
 * SED-FOUNDATION — evidence reference carried on a `verification-status` or
 * `open-question` workflow item. Mirrors `FindingClaimEvidence`
 * (`src/lib/findingClaim.ts`) so a report finding can be folded in verbatim.
 */
export interface WorkflowEvidenceRef {
  /** 1-based `[Ref N]` indices backing the claim. */
  refIds: number[];
  /** Verbatim supporting passage from the cited source. Non-empty when confirmed. */
  excerpt: string;
}

/**
 * SED-FOUNDATION — a unified evidence-workflow item on `SED.workflows`.
 *
 * Idempotency: `idempotencyKey` encodes `(reportId, reportVersion,
 * sedFieldPath)` (see `buildWorkflowIdempotencyKey`) so re-ingesting the same
 * report version SUPERSEDES its prior items rather than duplicating them, and
 * a NEW report version drops the stale ones (`shouldKeepWorkflowQuestion`).
 *
 * Every non-`id` field is documented but the SHAPE is intentionally broad:
 * one item carries either an open-question, a verification status, or a
 * siting note depending on `kind`. Validators (`validateWorkflowItem` et al.)
 * enforce the per-kind invariants.
 */
export interface EvidenceWorkflowItem {
  /** Stable id (caller-supplied or = `idempotencyKey`). */
  id: string;
  kind: EvidenceWorkflowKind;
  /** Dot-notation SED field path this item pertains to (e.g. `grid.capacity`). */
  sedFieldPath: string;
  /** Short human-readable subject (the question, the claim, or the note title). */
  title: string;
  /**
   * Lifecycle status:
   *  - `open`        — unresolved (open-question default).
   *  - `confirmed`   — verified WITH evidence (verification-status, site-visit note).
   *  - `pending`     — verification in progress / awaiting evidence.
   *  - `superseded`  — a newer report version replaced this item.
   *  - `dismissed`   — operator dismissed it (out of scope for foundation; reserved).
   */
  status: 'open' | 'confirmed' | 'pending' | 'superseded' | 'dismissed';
  /** Evidence backing a verification-status / answered open-question. */
  evidence?: WorkflowEvidenceRef;
  /** Structured, versioned siting notes (kind = 'siting-note'). Append-only. */
  sitingNotes?: SitingNoteEntry[];
  /** How the item was established (verification-status / siting-note). */
  verificationMethod?: WorkflowVerificationMethod;
  /**
   * Idempotency key `${reportId}__v${reportVersion}__${sedFieldPath}` for
   * report-sourced items. Absent for operator-authored / site-visit items
   * that did not originate from a report.
   */
  idempotencyKey?: string;
  /** Source report id (when report-derived). */
  reportId?: string;
  /** Source report version (participates in supersession + idempotency). */
  reportVersion?: number;
  /** ISO timestamp the item was created. */
  createdAt: string;
  /** ISO timestamp of the last mutation. */
  updatedAt?: string;
}

/** Climate evidence data. */
export interface ClimateEvidence {
  /** Validated server-only auxiliary TMY statistic; not a design percentile. */
  tmyHottestHours?: TmyHottestHoursSummary;
  avgTemp: number;          // °C, annual average
  peakTemp: number;         // °C, maximum recorded
  minTemp: number;          // °C, minimum recorded
  avgWetBulb: number;       // °C, annual average
  peakWetBulb: number;      // °C, worst-month wet bulb (design basis)
  humidity: number;         // %, annual average relative humidity
  annualPrecipitation: number; // mm
  monthlySummary: {
    month: string;
    avgTemp: number;
    maxTemp: number;
    minTemp: number;
    wetBulb: number;
    precipitation: number;
    coolingEfficiency: number; // 0-1
  }[];
  /**
   * EX-2 (EAS-FOLLOWUP-NOAA-CLIMATE) — auxiliary NOAA NCEI Climate
   * Normals 1991-2020 record. Populated when the producer ran the
   * NOAA fetcher (`services/dataProviders/climate/noaaNormalsFetch.ts`)
   * with the polygon centroid. Optional + additive — Open-Meteo
   * remains the PRIMARY source for `avgTemp` / `peakTemp` /
   * `monthlySummary`, and NOAA layers in the 30-year normals.
   * `designWetBulb` / `peakDesignWetBulb` are LEGACY names: they hold
   * Stull wet-bulb screening estimates from each month's mean
   * temperature normal at an assumed relative humidity, not ASHRAE
   * design percentiles or an equipment sizing basis (see
   * `services/dataProviders/climate/noaaNormalsBasis.ts`). Consumers
   * that want the NOAA normals read `noaaNormals`; everything that
   * reads the historical means continues to use `monthlySummary`
   * unchanged.
   *
   * Shape mirrors `NoaaNormalsResponse` in the fetcher module. The
   * SED schema inlines the structural type rather than importing it
   * so the climate section stays provider-agnostic (mirrors the
   * `attemptStages` and `solarResource` patterns added by EAS-4 / EX-1).
   */
  noaaNormals?: {
    stationId: string;
    stationName: string;
    stationDistanceKm: number;
    monthly: ReadonlyArray<{
      month: 'Jan' | 'Feb' | 'Mar' | 'Apr' | 'May' | 'Jun' | 'Jul' | 'Aug' | 'Sep' | 'Oct' | 'Nov' | 'Dec';
      avgTemp: number | null;
      maxTemp: number | null;
      minTemp: number | null;
      precipitationMm: number | null;
      designWetBulb: number | null;
    }>;
    peakDesignWetBulb: number | null;
    retrievedAt: string;
  };
}

/** Water stress evidence (WRI Aqueduct). */
export interface WaterStressEvidence {
  bwsScore: number | null;         // 0-5 normalized; null remains unavailable
  bwsCategory: number | null;      // 4.0: -1 arid, 0-4 ordinary categories
  bwsLabel: string;         // human-readable
  bwsRaw: number | null;           // actual ratio only; source sentinels stay in observations
  overallWaterRisk: number | null;  // independently sourced 0-5 composite
  stressLevel: 'Low' | 'Moderate' | 'High' | 'Unknown';
  sourceEdition?: '2.1' | '4.0';
  sourceQualification?: string[];
  studyAssessment?: import('./aqueduct4').Aqueduct4StudyAssessment;
}

/** Seismic evidence data. */
export interface SeismicEvidence {
  pga: number;              // g
  sds: number;              // g, design short-period
  sd1: number;              // g, design 1-second
  designCategory: string;   // A-F
  riskCategory: string;     // I-IV
  siteClass: string;        // A-F
}

/** Whether a duration describes this site's load service or contextual screening only. */
export type QueueDurationBasis = 'site-load-service' | 'regional-generation-benchmark' | 'proximity-heuristic';

/** Grid infrastructure evidence. */
export interface GridEvidence {
  substationName: string;
  substationDistKm: number;
  voltage: string;
  capacity: 'High' | 'Limited' | 'Constrained';
  complexity: 'Low' | 'Medium' | 'High';
  utilityName?: string;
  substationsWithin50km?: number;
  transmissionLines?: { voltage: string; distKm: number; owner?: string }[];
  nearestTransmissionKm?: number;
  nearestTransmissionVoltage?: string;
  estimatedInterconnectionCost?: string;
  estimatedInterconnectionCostBasis?: 'proximity-heuristic' | 'regional-generation-benchmark' | 'site-specific-utility';
  estimatedQueueTime?: string;
  /** Absent legacy basis is unverified context, never a site service schedule. */
  estimatedQueueTimeBasis?: QueueDurationBasis;
  isoRegion?: string;
  /**
   * KSD2 — provenance of `substationDistKm`. Stamped `'kmz-import'`
   * when the value was lifted from the KMZ-derived
   * `onSiteInfrastructure.substations[0].distanceKm` (user-authoritative);
   * stamped `'provider'` when the HIFLD value remains canonical.
   * Optional for back-compat with SEDs written before KSD2.
   */
  substationDistKmProvenance?: 'provider' | 'kmz-import';
  /**
   * KSI — operator/label parsed from the closest transmission-line KMZ
   * corridor's name (e.g. "Entergy 230kV Line"). Free-form, optional.
   */
  transmissionOperator?: string;
  /**
   * KSI — provenance of `nearestTransmissionKm` mirroring
   * `substationDistKmProvenance`. Stamped `'kmz-import'` when a KMZ
   * fiber/transmission corridor produced a closer distance than the
   * provider. Optional for back-compat.
   */
  nearestTransmissionKmProvenance?: 'provider' | 'kmz-import';
  /**
   * Structured utility-stated available capacity. ADDITIVE + presence-gated —
   * absent on legacy SEDs and whenever no document/user statement exists.
   *
   * Written by the utility-letter authored-schema transform
   * (`authoredSchemaToSed.ts`), which previously COLLAPSED the user's
   * `capacityOffered_MW` into the ordinal `capacity` band and destroyed the
   * number. The ordinal stays (legacy readers), the MW now rides here.
   *
   * NEVER derived from the HIFLD voltage-class proxy — that stays a
   * computed, labeled read-time proxy (`voltageToLineCapacityMw`); persisting
   * it would launder a proxy into a documented-looking field.
   */
  capacityDetail?: {
    /** Utility-stated available/offered capacity, MW. */
    mw: number;
    /**
     * What kind of statement produced the MW. A service letter is an
     * INDICATION, not a commitment; 'study' / 'executed' are reserved for
     * future interconnection-study / executed-agreement writers.
     */
    basis: 'utility-indication' | 'study' | 'executed';
    /** Which authored route entered it. */
    enteredVia: 'utility-letter';
  };
  /**
   * Interconnection service class from the utility letter — 'firm' vs
   * 'energy-only' etc. changes what the stated MW is worth on a 24/7 load.
   * Flat sibling (not inside `capacityDetail`) because authored-field
   * transforms are per-field. Optional for back-compat.
   */
  interconnectionClass?: 'firm' | 'non-firm' | 'cluster' | 'energy-only' | 'network';
}

/** Flood evidence (FEMA). */
export interface FloodEvidence {
  regulatoryContext?: import('./femaRegulatoryContext').FemaRegulatoryContext;
  floodZone: string;        // e.g. "X", "X500", "AE", "VE" — highest-risk intersecting zone
  sfha: boolean;            // true if any exact parcel area / fallback sample is in an SFHA
  floodRisk: 'Low' | 'Low-Medium' | 'Medium' | 'High';
  screeningCoverage?: import('./floodScreening').FloodScreeningCoverage;
  /**
   * Back-compatible zone fractions. Polygon-intersection results use exact
   * exclusive area; sample-sweep results use sample counts and may only be
   * narrated as an estimate. `analysisMethod` disambiguates the two.
   */
  zoneOverlap?: { zone: string; fraction: number; acres?: number }[];
  /** Number of points queried for point/sample methods; absent for exact area. */
  sampledPoints?: number;
  analysisMethod?: FloodAnalysisMethod;
  fallbackReason?: string;
  areaAnalysis?: FloodAreaAnalysis;
  /** Direct FEMA attributes only; never inferred from a range. `staticBfe`
   * is retained as the PR1 compatibility alias; new code uses explicit feet. */
  staticBfe?: number;
  staticBfeFt?: number;
  verticalDatum?: 'NAVD88' | 'NGVD29' | 'MLLW';
  firmPanelId?: string;
  effectiveDate?: string;
}

/** Both sample labels remain readable, but new provider output uses
 * `sample-sweep`; neither estimated method may drive acreage deductions. */
export type FloodAnalysisMethod =
  | 'point'
  | 'polygon-intersection'
  | 'sample-grid-estimate'
  | 'sample-sweep';

export interface FloodAreaMetric {
  acres: number;
  fraction: number;
}

/** Exact FEMA parcel-polygon result. The optional metric-object aliases retain
 * PR1 schema compatibility while the scalar fields support deterministic
 * closure checks and aggregation without repeated object unpacking. */
export interface FloodAreaAnalysis {
  method: 'polygon-intersection';
  parcelAreaAcres: number;
  mappedAreaAcres: number;
  unmappedAreaAcres: number;
  mappedFraction: number;
  unmappedFraction: number;
  onePercentAnnualChanceAcres: number;
  onePercentAnnualChanceFraction: number;
  zeroPointTwoPercentAnnualChanceAcres: number;
  zeroPointTwoPercentAnnualChanceFraction: number;
  floodwayAcres: number;
  floodwayFraction: number;
  zoneAreas: Array<{
    zone: string;
    annualChance: '1-percent' | '0.2-percent' | 'minimal' | 'undetermined';
    acres: number;
    fraction: number;
    sfha: boolean;
    floodway: boolean;
  }>;
  closureErrorFraction: number;
  /** PR1 compatibility aliases, populated for newly generated SEDs. */
  parcelAcres?: number;
  mapped?: FloodAreaMetric;
  unmapped?: FloodAreaMetric;
  onePercentAnnualChance?: FloodAreaMetric;
  zeroPointTwoPercentAnnualChance?: FloodAreaMetric;
  regulatoryFloodway?: FloodAreaMetric;
  /** Present only when the exact pairwise computation failed inside
   * polygon-clipping and a disclosed fallback tier produced this result. */
  geometryTier?: 'single-sweep' | 'precision-snap';
  /** Present only with geometryTier 'precision-snap': the fixed coordinate
   * grid (degrees) the analysis was recomputed on. */
  precisionSnapDeg?: number;
  /** Present only when a fallback tier erased a positive sliver that an exact
   * per-feature clip still finds (acres, lower bound of the 1% acreage). */
  exactPositiveFloorAcres?: number;
  /** Positive features left out of that floor because their own exact clip
   * failed; present only when non-zero. */
  exactPositiveFloorSkippedFeatures?: number;
  /** Present only when a fallback tier understated the regulatory floodway
   * (acres, the largest exactly clipped floodway feature). */
  exactFloodwayFloorAcres?: number;
  /** Present only when the SFHA flag, risk and headline zone were restored
   * from an exact per-feature clip while the acreage stayed within tolerance. */
  exactPositiveHeadlineRestored?: true;
}

/** RD-1 (D) — EPA FRS regulated-facility screen, merged into the
 *  environmental section as a secondary contribution. Counts are within
 *  radii of the site centroid; the snapshot is a point dataset. */
export interface EnvironmentalRegulatedFacilities {
  within1MiCount: number;
  within3MiCount: number;
  nearest: {
    name: string;
    programs: string[];
    distanceMi: number;
  } | null;
  /** Facility counts per EPA program acronym within 3 mi (RCRA, NPDES, AIR, SEMS, TRI, BROWNFIELD). */
  programCounts: Record<string, number>;
  truncated: boolean;
  dataVintage: string;
}

export interface PadusUnavailableSourceUnit {
  sourceFeatureId: string;
  name: string;
  reason: string;
  nativeBounds?: number[];
  rawSourcePin?: { path: string; generation: string; bytes: number; sha256: string };
  sourceQualificationReceiptHash?: string;
  parcelIds: string[];
  intersectsSiteScope: boolean;
}

/** RD-1 (D) — PAD-US protected-area screen (fee/easement/designation units).
 *  Replaces the LLM-free-text-only coverage with structured data. */
export interface EnvironmentalProtectedAreas {
  onParcel: Array<{ name: string; manager: string | null; designation: string | null; gapStatus: string | null }>;
  within1Mi: Array<{ name: string; manager: string | null; designation: string | null; gapStatus: string | null }>;
  nearestDistanceMi: number | null;
  dataVintage: string;
  /** Original invalid units are query-scoped uncertainty, never observed overlaps. */
  unavailableSourceUnits?: PadusUnavailableSourceUnit[];
  /** Missing on legacy data. Empty inventory queries cannot prove absence. */
  coverage?: {
    status: 'complete' | 'partial' | 'unknown';
    reason: string;
    sourceVersion: string;
    negativeFindingQualified: boolean;
    sourceQualificationReceiptHash?: string;
    queryScope: 'point' | 'polygon' | 'multiple-parcels';
    queriedParcels: Array<{ id: string; status: 'queried' | 'partial' | 'unavailable'; observedUnitIds: string[]; unavailableSourceUnitIds?: string[]; reason?: string }>;
    resultTruncated: boolean;
  };
}

/** RD-1 (D) — Census AIANNH tribal/native-lands screen. Placed on the
 *  environmental section (NOT culturalResources, which is prod-gated):
 *  tribal-land overlap is a hard jurisdictional siting factor. The
 *  LLM narrative in siteResearch.tribalLands remains the complement. */
export interface EnvironmentalTribalLands {
  onParcel: boolean;
  areas: Array<{ name: string; geoid: string; kind: string | null }>;
  nearestDistanceMi: number | null;
  dataVintage: string;
}

/** Environmental constraints evidence. */
export interface EnvironmentalEvidence {
  /** Mapped final OR proposed habitat; undefined means the source screen is incomplete. */
  hasCriticalHabitat?: boolean;
  criticalHabitatFindings?: import('./refdata').CriticalHabitatFinding[];
  criticalHabitatCoverage?: import('./refdata').CriticalHabitatCoverage;
  species: string[];
  hasWetlands?: boolean;
  wetlandType?: string;
  wetlandAcres?: number;
  /** RD-1 (D) — optional structured blocks from the refdata-only secondary
   *  providers (epa-frs / padus-protected / census-aiannh). Absent on
   *  legacy SEDs and when the datasets are unseeded. */
  regulatedFacilities?: EnvironmentalRegulatedFacilities;
  protectedAreas?: EnvironmentalProtectedAreas;
  tribalLands?: EnvironmentalTribalLands;
  /**
   * EPA Green Book NAYRO county NAAQS designations (`epa-attainment`).
   * Absent on legacy SEDs and when the lookup did not run; read through
   * `readEpaDesignation` (`src/lib/sed/epaDesignation.ts`) — absence and a
   * block without `coverage` are "not assessed", never attainment.
   */
  naaqsDesignations?: import('../services/dataProviders/epaAttainment').EpaAttainmentData;
  wetlandAcreageBasis?: 'source-feature-total';
  wetlandSourceAcres?: Record<string, number>;
  wetlandSourceAcreageConflicts?: import('./refdata').NwiSourceAcreageConflict[];
  wetlandAcreageIssues?: import('./refdata').NwiAcreageIssue[];
  wetlandMappingCoverage?: import('./refdata').NwiMappingCoverage;
}

/** Reactor design class stamped from Project Intent (site-level). */
export interface TechnologyEvidence {
  reactorDesignClass: import('../config/reactorDesignCatalog').ReactorDesignClassId;
  reactorDesignLabel: string;
  fuelClass: 'LEU' | 'HALEU' | 'mixed' | 'unknown';
  licensingPathwaySummary?: string;
  /** Mirror of site intent at stamp time */
  nuclearInterest?: string;
  typicalModuleMWe?: number;
  typicalCodYears?: { low: number; high: number };
  reactorVendorId?: string;
  reactorVendorLabel?: string;
  nrcDocketId?: string;
}

/**
 * Cooling evidence (v1.2.1 HR-5 P2-2) — first-class BTM cooling section.
 *
 * Captures the THREE ORTHOGONAL cooling axes (do NOT collapse — codebase
 * invariant; see `siteRag.ts` BopHeatRejection / CoolingGeneration docs):
 *   - `coolingStrategy`   — rack / IT-side heat-CAPTURE loop (air vs liquid).
 *   - `bopHeatRejection`  — cold-END heat-rejection SINK (drives water withdrawal).
 *   - `coolingGeneration` — BTM cold-PRODUCTION machine (absorption / vapour-
 *     compression / steam-driven / free-cooling).
 * Plus the resolved PUE (provenance-tagged), the water-withdrawal estimate
 * keyed off the SINK, and the waste-heat-utilisation credit eligibility.
 *
 * Intent-stamped (status `'verified-from-intent'`) — sourced from the site's
 * Project Intent, NOT a live provider. ADDITIVE + optional on the SED.
 */
export interface CoolingEvidence {
  /** Rack / IT-side heat-capture loop. */
  coolingStrategy: 'air' | 'liquid' | 'hybrid' | 'undecided';
  /** Cold-end heat-rejection SINK (the water-withdrawal axis). */
  bopHeatRejection?: import('./siteRag').BopHeatRejection;
  /** BTM cold-production machine. Resolved via `projectCoolingGeneration`. */
  coolingGeneration?: import('./siteRag').CoolingGeneration;
  /** Resolved PUE (declared > config-derived > base estimate); null when unknown. */
  resolvedPue: number | null;
  /** Provenance of `resolvedPue`. */
  pueBasis: 'declared' | 'config-derived' | 'base-estimate' | 'unavailable';
  /**
   * Water-withdrawal ESTIMATE keyed off the cold-end SINK. `litersPerMwhThermal`
   * is a sink-class estimate (dry≈0, wet-tower≈high, hybrid≈mid, once-through≈
   * withdrawal-not-consumption) — flagged via `basis`. Null when no sink declared.
   */
  waterWithdrawal?: {
    litersPerMwhThermal: number | null;
    basis: string;
    sink: import('./siteRag').BopHeatRejection;
  } | null;
  /**
   * Waste-heat-utilisation credit eligibility — only meaningful when the
   * cooling-generation machine is thermal (absorption / steam-driven) AND a
   * `wasteHeatOfftake` is declared. `eligible` gates the credit.
   */
  wasteHeatCredit?: {
    eligible: boolean;
    heatUsableFraction: number | null;
    thermalLoadMwth: number | null;
    offtakeStatus: string;
  } | null;
}

/**
 * Power-semantics evidence (v1.2.1 HR-5 P2-3) — basis-explicit power.
 *
 * Makes the declared MW figure basis-explicit (IT-load vs facility vs MVA)
 * via `resolvePowerStack`, so reports never present an IT load as a grid
 * demand (or vice-versa). Intent-stamped. ADDITIVE + optional.
 */
export interface PowerSemanticsEvidence {
  powerBasis: 'it-load' | 'facility' | null;
  /** The entered MW figure (powerRequirementMW). */
  declaredMw: number | null;
  pueValue: number | null;
  powerFactor: number | null;
  /** Derived layers via `resolvePowerStack`. */
  itLoadMw: number | null;
  facilityMw: number | null;
  apparentMva: number | null;
  /** Whether the basis was declared by the user or a default was applied. */
  basisProvenance: 'declared' | 'default-applied' | 'user-unknown';

  /**
   * How the figure was established. ABSENT on every SED written before bucket
   * bands existed ⇒ read as 'exact'.
   */
  figureBasis?: 'exact' | 'bucket-derived';

  /**
   * BUCKET-DERIVED band, populated only when `figureBasis === 'bucket-derived'`.
   *
   * INVARIANT — when this is present, every SCALAR field above (`declaredMw`,
   * `itLoadMw`, `facilityMw`, `apparentMva`) stays NULL. Those scalars feed
   * TIER-1 GOVERNED FACTS in `factDescriptors.ts`; leaving them null is what
   * stops a bucket-derived range from ever resolving as a verified point value.
   * The ingestion engine's "a range never collapses into a scalar" rule is
   * preserved by construction, not by a runtime check.
   *
   * `low` / `high` are null at an open band edge (`under-10mw` has no floor,
   * `over-1500mw` has no ceiling) — never 0, never Infinity.
   */
  bucketBand?: {
    bucket: string;
    bucketLabel: string;
    openLow: boolean;
    openHigh: boolean;
    /** The documented projection finance / BoM / firm-capacity already use. */
    sizingBasisMw: number;
    sizingBasisRule: 'bucket-midpoint' | 'bucket-max-open-low' | 'bucket-floor-open-high';
    enteredMw: { low: number | null; high: number | null };
    itLoadMw: { low: number | null; high: number | null };
    facilityMw: { low: number | null; high: number | null };
    apparentMva: { low: number | null; high: number | null };
    generationRequiredMw: { low: number | null; high: number | null };
  } | null;
}

/**
 * Firm-capacity (ELCC) evidence (v1.2.1 HR-5 P2-3) — accreditation intent.
 * Intent-stamped from `capacityCreditIntent`. ADDITIVE + optional.
 */
export interface FirmCapacityEvidence {
  accreditationTarget: 'none' | 'partial' | 'firm' | 'undecided';
  targetMw: number | null;
  accreditationNote: string | null;
  /** Reuses `grid.isoRegion` when present. */
  isoRegion?: string | null;
}

/**
 * LCOE evidence (v1.2.1 HR-5 P2-3) — economics anchor with explicit fuel-cost
 * grounding. `fuelCostBasis` flips to `'provider-grounded'` when P2-4's gas
 * provider filled the fuel cost; otherwise `'user-declared'` or `'unavailable'`
 * so the LLM never presents an assumed gas LCOE as grounded. ADDITIVE + optional.
 */
export interface LcoeEvidence {
  fuelCostPerMwhUsd: number | null;
  fuelCostBasis: 'provider-grounded' | 'user-declared' | 'unavailable';
  discountRate: number | null;
  lifetimeYears: number | null;
  capacityFactorPct: number | null;
  blendedLcoePerMwhUsd: number | null;
  lcoeBasis: 'computed' | 'partial' | 'unavailable';
}

/**
 * SED D2 — one parsed ISO/RTO interconnection-queue position, surfaced on
 * `InterconnectionEvidence`. INLINES the structural shape of
 * `QueuePosition` from `services/dataProviders/utility/types.ts` rather
 * than importing it, so the SED schema stays provider-agnostic (mirrors
 * the `noaaNormals` / `attemptStages` inline conventions). Every numeric
 * field is `Number.isFinite()`-guarded at the provider boundary; null
 * here means "the ISO did not publish it" — never a fabricated zero.
 */
export interface InterconnectionQueueEntry {
  /** Queue id assigned by the ISO (e.g. "AC2-039"). */
  queueId: string;
  /** ISO / RTO label (mirrors the `IsoId` union, kept as string here). */
  iso: 'PJM' | 'MISO' | 'ERCOT' | 'CAISO' | 'NYISO' | 'ISO-NE' | 'SPP' | 'other';
  /** Date the request entered the queue (ISO-8601). Null when unpublished. */
  queueDate: string | null;
  /** Requested megawatts. Null when the ISO did not publish it. */
  requestedMW: number | null;
  /** Project type (solar / wind / gas / storage / hybrid / load), raw upstream. */
  projectType: string | null;
  /** Current cluster phase ("Cluster Study", "Suspended", …), raw upstream. */
  status: string | null;
}

/**
 * SED D2 — typed grid-interconnection evidence (ISO/RTO queue position,
 * MW, study phase, est cost, queue duration). Grounds the
 * power-availability verdict, REPLACING the prior ungrounded AI narrative
 * about "where the project sits in the queue".
 *
 * PROVENANCE CONTRACT:
 *   - `'intent'` — only the user's declared `ProjectIntent.interconnectionStatus`
 *     was available (no provider queue rows for this ISO).
 *   - `'provider'` — the ISO-queue provider returned ≥1 parsed queue row.
 *   - `'intent+provider'` — both. Provider provenance is GATED on
 *     `queuePositions.length > 0`: an empty queue NEVER asserts a provider
 *     grounding (HARD RULE 8 — never fabricate a queue).
 *
 * Distinct from `GridEvidence` (HIFLD substation / transmission proximity):
 * this section is the ISO-queue + declared-status slice only. ADDITIVE +
 * optional on the SED — legacy SEDs carry it `undefined`.
 */
export interface InterconnectionEvidence {
  /** Resolved ISO/RTO for the site's state. `null` when unresolved. */
  iso: 'PJM' | 'MISO' | 'ERCOT' | 'CAISO' | 'NYISO' | 'ISO-NE' | 'SPP' | 'other' | null;
  /**
   * User-declared utility-service / interconnection milestone. Executed load
   * service does not settle a separate generator request or prove energization.
   * `null` when the intent did not declare one.
   */
  queueStatus: 'none' | 'pre-application' | 'queued' | 'study' | 'executed-LGIA' | 'executed-load-service' | null;
  /** Optional ISO/utility queue position identifier (free-text, from intent). */
  queueId: string | null;
  /** Current or last accepted study phase, retained after agreement acceptance. */
  studyPhase: 'feasibility' | 'system-impact' | 'facilities' | null;
  /** Requested MW for THIS project, from intent. Null when undeclared. */
  mwRequested: number | null;
  /**
   * Estimated interconnection cost ($/kW). Null — there is no in-repo
   * deterministic estimator for this; flagged as a follow-up data source
   * so the narrative never fabricates a figure.
   */
  estInterconnectionCostUsdPerKw: number | null;
  /** How `estInterconnectionCostUsdPerKw` was sourced. */
  costBasis: 'provider' | 'user-declared' | 'unavailable';
  /**
   * Median queue duration (days, P50) for this ISO. Null — no in-repo
   * deterministic duration model; flagged follow-up.
   */
  queueDurationP50Days: number | null;
  queueDurationBasis?: QueueDurationBasis;
  /** Count of parsed queue positions for the site's ISO. Null when no provider run. */
  queuePositionCount: number | null;
  /** Total MW across the parsed queue positions. Null when no provider run. */
  totalQueueMw: number | null;
  /**
   * Parsed ISO-queue positions (capped sample). Empty/absent when the
   * provider returned none — NEVER fabricated.
   */
  queuePositions?: ReadonlyArray<InterconnectionQueueEntry>;
  /** Vintage of the queue data (ISO snapshot date / fetch date), when known. */
  queueDataVintage: string | null;
  /** Where the section's facts came from. Provider GATED on queue rows > 0. */
  provenance: 'intent' | 'provider' | 'intent+provider';
}

/**
 * SED D2 — typed IRA-incentives evidence (§45Y / §48E energy-community +
 * domestic-content + prevailing-wage/apprenticeship adders). Sourced from
 * the pure `calculateTaxCredits` calculator off `financialAssumptions`,
 * REPLACING ungrounded AI narrative about tax-credit eligibility.
 *
 * HONESTY CONTRACT:
 *   - `energyCommunityFlag` DEFAULTS `false` with `energyCommunityBasis:
 *     'unresolved'` + a `disclaimer`. There is NO in-repo geospatial
 *     energy-community (census-tract) resolver, so the designation is
 *     NEVER fabricated — it is flagged as a follow-up data source.
 *   - When the route is `'none'` (no qualifying technology, or a
 *     `mixed`/`tbd` campus tech, or missing in-service-date / capacity),
 *     the section is honestly empty — no credit values are asserted.
 *
 * ADDITIVE + optional on the SED.
 */
export interface IncentivesEvidence {
  /** Governing IRC section the calculator routed to. `'none'` = no qualifying credit. */
  citedSection: '§45Y' | '§48E' | '§45U' | '§45' | '§48' | 'none';
  /** §45Y / §45U PTC year-1 nominal $/MWh. Null on an ITC / 'none' route. */
  ptcPerMwhUsd: number | null;
  /** §48E ITC %. Null on a PTC / 'none' route. */
  itcPct: number | null;
  /**
   * Energy-community designation. DEFAULTS `false` — never fabricated
   * (no in-repo census-tract resolver). See `energyCommunityBasis`.
   */
  energyCommunityFlag: boolean;
  /** How `energyCommunityFlag` was resolved. `'unresolved'` = follow-up data source. */
  energyCommunityBasis: 'user-declared' | 'unresolved';
  /** Domestic-content adder eligibility (≥40% threshold). `null` = undeclared. */
  domesticContent: boolean | null;
  /**
   * Prevailing-wage + apprenticeship compliance. `null` = UNDECLARED, which is
   * the normal state — nothing in the product asks for it yet.
   *
   * This was `boolean` defaulting to `false`, and the comment said "Default
   * false (undeclared)": the two meanings were conflated in the type itself.
   * `false` reads as an assertion of non-compliance, and it resolved the §48E
   * rate to 6% in published evidence while the FA engine used the user's own
   * declared `itcPct`. Undeclared must be representable, or the record lies.
   */
  pwApprenticeship: boolean | null;
  /** True when PW/A was undeclared, so no statutory rate could be resolved. */
  rateWithheld: boolean;
  /** Why the rate was withheld and what to declare to resolve it. */
  withheldReason: string | null;
  /** Per-adder breakdown the calculator emitted. */
  bonusBreakdown: ReadonlyArray<{
    name: string;
    deltaPct?: number;
    deltaPerMwh?: number;
    condition: string;
  }>;
  /** IRC §§ citations, e.g. ["IRC §45Y …", "IRC §6417 …"]. */
  citations: ReadonlyArray<string>;
  /** §6417 elective-payment / direct-pay eligibility. */
  directPayEligible: boolean;
  /** Standing honesty disclaimer (estimate; energy-community unresolved; confirm with counsel). */
  disclaimer: string;
}

/**
 * SED D2 (H-6) — FEMA National Risk Index hazard rating union. Verbatim
 * mirror of the NRI `*_RISKR` rating taxonomy (`Very High` …
 * `No Rating` / `Not Applicable` / `Insufficient Data`). Kept as a string
 * union — every value here is a REAL rating FEMA publishes; we never coin
 * a label. `null` upstream (unmapped county / missing hazard) maps to
 * `MultiHazardRating | null` on the typed leaf, never a fabricated rating.
 */
export type MultiHazardRating =
  | 'Very High'
  | 'Relatively High'
  | 'Relatively Moderate'
  | 'Relatively Low'
  | 'Very Low'
  | 'No Rating'
  | 'Not Applicable'
  | 'Insufficient Data';

/**
 * SED D2 (H-6) — one parsed FEMA National Risk Index hazard slice, surfaced
 * on `MultiHazardEvidence.hazards`. INLINES the structural shape the NRI
 * county FeatureServer publishes per hazard (`<HZ>_RISKR` / `<HZ>_RISKS` /
 * `<HZ>_AFREQ`) rather than importing the provider type, so the SED schema
 * stays provider-agnostic (mirrors the `InterconnectionQueueEntry` inline
 * convention).
 *
 * HONESTY CONTRACT: every numeric field is `Number.isFinite()`-guarded at the
 * provider boundary; `null` here means "FEMA did not publish it for this
 * county" — never a fabricated zero. `rating` is `null` when the hazard
 * carries `No Rating` / `Insufficient Data` so the narrative never asserts a
 * rating FEMA itself declined to give.
 */
export interface MultiHazardEntry {
  /** Hazard key — NRI hazard prefix, lowercased ('wildfire' | 'strong-wind' | 'hail' | 'drought'). */
  hazard: 'wildfire' | 'strong-wind' | 'hail' | 'drought';
  /** NRI composite Hazard Type Risk Index rating (`*_RISKR`). Null when unrated. */
  rating: MultiHazardRating | null;
  /** NRI Hazard Type Risk Index score 0-100 (`*_RISKS`, national percentile). Null when unpublished. */
  score: number | null;
  /** NRI annualized frequency (`*_AFREQ`, events/yr). Null when unpublished. */
  annualizedFrequency: number | null;
}

/**
 * SED D2 (H-6) — typed MULTI-HAZARD evidence beyond the SED's existing
 * `flood` (FEMA NFHL) + `seismic` (USGS) sections. Sourced from the
 * deterministic FEMA National Risk Index county FeatureServer
 * (`femaNriProvider`): wildfire / strong-wind / hail / drought hazard
 * ratings + the composite Risk Index rating, REPLACING ungrounded AI
 * narrative about "regional hazard exposure".
 *
 * PROVENANCE CONTRACT:
 *   - `'provider'` — the NRI FeatureServer returned a county feature with
 *     ≥1 parsed hazard rating. The ONLY grounding path: there is no intent
 *     declaration for multi-hazard, so the section is either provider-
 *     grounded or honestly `unavailable`.
 *   - honest `unavailable` — no county feature intersected the site
 *     (off-territory / non-US — NRI is US counties + territories only),
 *     the provider was not run, or it was rate-limited. NEVER fabricated.
 *
 * DEGRADES EXPLICITLY for non-US / off-territory sites: NRI has no coverage,
 * so the section is `unavailable` with a reason naming the absence rather
 * than an asserted "Very Low" hazard. ADDITIVE + optional on the SED.
 */
export interface MultiHazardEvidence {
  /** FEMA NRI composite Risk Index rating for the county (`RISK_RATNG`). Null when unrated. */
  compositeRiskRating: MultiHazardRating | null;
  /** FEMA NRI composite Risk Index score 0-100 (`RISK_SCORE`). Null when unpublished. */
  compositeRiskScore: number | null;
  /** FEMA NRI composite state-percentile (`RISK_SPCTL`). Null when unpublished. */
  compositeStatePercentile: number | null;
  /** Two-letter state/territory the matched county sits in (`STATEABBRV`). Null when unmatched. */
  stateAbbrev: string | null;
  /** Matched county name (`COUNTY`). Null when unmatched. */
  countyName: string | null;
  /**
   * Parsed per-hazard slices (wildfire / strong-wind / hail / drought).
   * Empty/absent when the county feature carried none — NEVER fabricated.
   */
  hazards: ReadonlyArray<MultiHazardEntry>;
  /** Source dataset vintage (NRI release the FeatureServer serves, e.g. '2023'). Null when unknown. */
  datasetVintage: string | null;
  /** Where the section's facts came from. Provider-grounded only (no intent axis). */
  provenance: 'provider';
}

/**
 * SED D2 (H-6) — typed TRANSMISSION-HEADROOM evidence (Available Transfer
 * Capability MW, distribution hosting-capacity MW, nodal LMP $/MWh +
 * congestion frequency). Intended to REPLACE ungrounded AI narrative about
 * "how much room is on the wires" with sourced figures.
 *
 * HONEST-UNAVAILABLE CONTRACT (zero-unverified North Star): there is NO clean,
 * point-queryable public API for any of these in-repo today —
 *   - ATC MW: ISO/RTO OASIS nodes (NERC `oasis.*` per-RTO endpoints) require
 *     per-RTO scraping + auth + a path/flowgate model, not a point lookup.
 *   - Hosting capacity: published as per-utility ArcGIS hosting-capacity maps
 *     (e.g. PG&E ICA, ComEd, Eversource) with NO national schema.
 *   - Nodal LMP / congestion: ISO settlement feeds (MISO/PJM/CAISO LMP APIs)
 *     are per-RTO + node-id keyed, not address-resolvable without a node map.
 * So the provider returns an HONEST `unavailable` status + a `disclaimer`
 * naming the real sources as follow-up. Every value field stays `null` — a
 * figure is NEVER fabricated or AI-inferred. When/if a real fetcher lands,
 * it flips `provenance` to `'provider'` and fills the nulls.
 *
 * ADDITIVE + optional on the SED — legacy SEDs carry it `undefined`.
 */
export interface TransmissionHeadroomEvidence {
  /** Available Transfer Capability (MW) at the nearest interconnection point. Null — no in-repo source. */
  atcMw: number | null;
  /** Distribution feeder / substation hosting capacity (MW). Null — no national in-repo source. */
  hostingCapacityMw: number | null;
  /** Representative nodal Locational Marginal Price ($/MWh). Null — no in-repo node-resolved source. */
  nodalLmpUsdPerMwh: number | null;
  /** Fraction of hours the nearest node is congestion-bound (0..1). Null — no in-repo source. */
  congestionFrequency: number | null;
  /** How the headroom figures were sourced. `'unavailable'` until a real fetcher lands. */
  headroomBasis: 'provider' | 'unavailable';
  /** Where the section's facts came from. Honest stub today. */
  provenance: 'provider' | 'unavailable';
  /** Honesty disclaimer naming the real follow-up sources (OASIS ATC / utility hosting-capacity maps / ISO LMP). */
  disclaimer: string;
}

/**
 * SED D2 (H-6) — typed WATER-RESOURCE evidence (consumptive-use allotment AFY,
 * water-rights status, aquifer depth-to-water ft, NPDES discharge permit).
 * Intended to REPLACE ungrounded AI narrative about "water availability +
 * discharge feasibility" with sourced figures.
 *
 * Distinct from the SED's existing `waterStress` section (WRI Aqueduct basin
 * STRESS index): this section is the site-specific WITHDRAWAL / RIGHTS /
 * AQUIFER / DISCHARGE slice, not a basin risk band.
 *
 * HONEST-UNAVAILABLE CONTRACT (zero-unverified North Star): there is NO clean,
 * point-queryable public API for any of these in-repo today —
 *   - Consumptive-use allotment / water rights: STATE-administered (e.g. TX
 *     TCEQ water-rights DB, CO DWR, CA eWRIMS) — no national point API.
 *   - Aquifer depth-to-water: USGS NWIS groundwater site service is keyed by
 *     monitoring-well site id, not address — needs a nearest-well resolver.
 *   - NPDES discharge: EPA ECHO / NPDES is permit-id keyed; a point→permit
 *     join requires the ICIS-NPDES facility geography layer.
 * So the provider returns an HONEST `unavailable` status + a `disclaimer`
 * naming the real sources as follow-up. Every value field stays `null` — a
 * figure is NEVER fabricated or AI-inferred. When/if a real fetcher lands,
 * it flips `provenance` to `'provider'` and fills the nulls.
 *
 * ADDITIVE + optional on the SED — legacy SEDs carry it `undefined`.
 *
 * Document-intelligence plan P6 (2026-09-07): municipal water supply and
 * wastewater service are the two facts a city or district actually states
 * in a service letter or questionnaire. They live in optional sub-objects
 * so the honest-null contract above is untouched; `waterResourceBasis` /
 * `provenance` widen to `'document' | 'authored'` when a sub-object was
 * filled from an upload or an authored claim. Every sub-object carries its
 * own attribution (who said it, in what role, when, for which project).
 */
export interface WaterEvidenceProvenance {
  /** Person or body that stated the figures (e.g. 'past Director of Water'). */
  statedBy?: string | null;
  /** Their role or capacity (e.g. 'Director of Utilities'). */
  statedByRole?: string | null;
  /** Organisation (e.g. 'City of DeRidder'). */
  organisation?: string | null;
  /** Date stated, ISO or as written in the source. */
  statedOn?: string | null;
  /** Whether the statement was prepared for THIS site or re-used from another project. */
  appliesToProject?: 'this-site' | 'other-project' | 'unknown';
  /** Upload the figures were extracted from, when document-derived. */
  documentId?: string | null;
  documentName?: string | null;
  /** Authored claim id, when user-authored. */
  claimId?: string | null;
  note?: string | null;
}

/** Municipal / district potable-water service as stated by the provider. */
export interface MunicipalWaterSupply {
  provider: string | null;
  /** Source description (e.g. 'groundwater from the Chicot Aquifer'). */
  sourceDescription?: string | null;
  avgMgd: number | null;
  peakMgd: number | null;
  permittedMgd: number | null;
  excessMgd: number | null;
  storageGallons?: number | null;
  /** Distance from the site to the nearest main (miles). */
  nearestMainMiles: number | null;
  /** Whether a line already reaches the site. */
  lineToSite: boolean | null;
  /** Usually null for supply; kept symmetric with wastewater. */
  connectionType: 'gravity' | 'force-main' | null;
  notes?: string | null;
  provenance?: WaterEvidenceProvenance | null;
}

/** Municipal / district wastewater (sewer) service as stated by the provider. */
export interface WastewaterService {
  provider: string | null;
  /** Plant description (e.g. 'activated sludge'). */
  sourceDescription?: string | null;
  avgMgd: number | null;
  peakMgd: number | null;
  permittedMgd: number | null;
  excessMgd: number | null;
  storageGallons?: number | null;
  /** Distance from the site to the nearest connection / lift station (miles). */
  nearestMainMiles: number | null;
  lineToSite: boolean | null;
  connectionType: 'gravity' | 'force-main' | null;
  notes?: string | null;
  provenance?: WaterEvidenceProvenance | null;
}

export interface WaterResourceEvidence {
  /** Consumptive-use allotment (acre-feet/yr). Null — state-administered, no in-repo source. */
  consumptiveUseAfy: number | null;
  /** Water-rights status (free-text upstream, e.g. 'senior' / 'junior' / 'permitted'). Null — no in-repo source. */
  waterRightsStatus: string | null;
  /** Aquifer depth-to-water (ft below ground surface). Null — USGS NWIS keyed by well id, no in-repo resolver. */
  aquiferDepthToWaterFt: number | null;
  /** NPDES discharge permit identifier (EPA ECHO / ICIS). Null — permit-id keyed, no in-repo point join. */
  npdesPermitId: string | null;
  /** How the water-resource figures were sourced. `'unavailable'` until a real fetcher lands;
   *  `'document'` / `'authored'` once a municipal sub-object was filled from an upload or a claim. */
  waterResourceBasis: 'provider' | 'unavailable' | 'document' | 'authored';
  /** Where the section's facts came from. Honest stub today. */
  provenance: 'provider' | 'unavailable' | 'document' | 'authored';
  /** Honesty disclaimer naming the real follow-up sources (state water-rights DB / USGS NWIS / EPA ECHO NPDES). */
  disclaimer: string;
  /** RD-1 (D) — watershed identity from the WBD snapshot (usgs-waters provider). */
  huc8?: { code: string; name: string | null } | null;
  huc10?: { code: string; name: string | null } | null;
  huc12?: { code: string; name: string | null } | null;
  /** RD-1 (D) — principal aquifer underlying the site (USGS), null when none mapped. */
  principalAquifer?: { name: string } | null;
  /** RD-1 (D) — EPA sole-source aquifer overlay, null when none. */
  soleSourceAquifer?: { name: string } | null;
  /** P6 — municipal potable-water service as stated by the provider (document or authored). */
  municipalSupply?: MunicipalWaterSupply | null;
  /** P6 — municipal wastewater service as stated by the provider (document or authored). */
  wastewaterService?: WastewaterService | null;
}

/**
 * Gas-infrastructure proximity (Tier 1, 2026-06) — compressor stations /
 * pipeline interconnects / receipt-delivery points from the EIA/HIFLD ArcGIS
 * layers on the ORNL host (`services5.arcgis.com/HDRa0B57OVrv2E1q`).
 *
 * HONESTY CONTRACT: these are operator/state-level CONTEXT signals for tap
 * feasibility — NOT site-deliverable capacity. Pipeline delivery pressure,
 * receipt-point MAOP, and firm transport availability are not public data;
 * firm capacity requires direct operator engagement + an interconnect study.
 * All numbers nullable — a missing layer or sentinel value (-999 / 9999999)
 * yields null, never a fabricated zero. `dataVintage` MUST be stamped
 * wherever these values are narrated (the source layers' `dataLastEditDate`
 * is 2021-02-25 — stale vintage).
 */
export interface GasInfraProximity {
  /** Distance (km) from site centroid to the nearest gas compressor station. */
  nearestCompressorKm: number | null;
  /** Certificated horsepower (CERT_HP) of that nearest compressor station. */
  nearestCompressorCertHp: number | null;
  /** Distance (km) to the nearest oil & natural-gas pipeline interconnect. */
  nearestInterconnectKm: number | null;
  /** Distance (km) to the nearest gas receipt/delivery point. */
  nearestReceiptPointKm: number | null;
  /** MAXCAP of that nearest receipt point, converted Mcf/d → MMcf/d. */
  receiptPointMaxCapMmcfd: number | null;
  /** Source-layer data vintage (ArcGIS dataLastEditDate 2021-02-25). */
  dataVintage: '2021-02';
}

/**
 * PHMSA operator system stats (Tier 2 gas enrichment, 2026-06) — the
 * nearest pipeline operator's reported transmission system in the site's
 * state, from the PHMSA F7100.2-1 gas transmission & gathering annual
 * report (operator × state granularity; Catalyst Cooperative Zenodo
 * mirror DOI 10.5281/zenodo.7683351; US-gov public domain).
 *
 * HONESTY CONTRACT:
 *  - `matchBasis` records HOW the operator label was joined: 'exact'
 *    (deterministic name normalization), 'alias' (curated alias table —
 *    `src/data/phmsaOperatorAliases.json`), or 'none' (no match — every
 *    stats field is null; narration states the absence). NO runtime
 *    fuzzy matching beyond the committed alias table.
 *  - These are operator/state-level CONTEXT signals (system heft, age,
 *    stress class) — NOT site-deliverable capacity, pressure, or firm
 *    transport. The Tier 1 deliverability caveat still applies.
 *  - `reportYear` is the PHMSA filing vintage and MUST be cited
 *    wherever these values are narrated.
 */
export interface OperatorSystemStats {
  /** PHMSA operator name as filed (PARTA2NAMEOFCOMP). Null when unmatched. */
  matchedName: string | null;
  matchBasis: 'exact' | 'alias' | 'none';
  /** PHMSA annual-report year the bundled dataset was built from. */
  reportYear: number;
  /** Two-letter state code the stats row was filed for. */
  state: string | null;
  /** True when any interstate row contributed in this state. Null when unmatched. */
  interstate: boolean | null;
  /** Onshore transmission miles in `state`, collapsed to four NPS classes. */
  milesByDiameterClass: {
    lt12: number;
    '12to20': number;
    '20to30': number;
    gte30: number;
  } | null;
  /** Reported onshore transmission total (Part H) in `state`. */
  totalOnshoreTransmissionMiles: number | null;
  /** Share of classified onshore miles that are ≥20-inch NPS (0-100). */
  shareGte20inPct: number | null;
  /** Share of onshore steel miles operating at >50 %SMYS (Part K, 0-100). */
  shareSmysGte50Pct: number | null;
  /** Share of onshore transmission miles installed pre-1970 (Part J, 0-100). */
  sharePre1970Pct: number | null;
}

/**
 * One EIA state-to-state border-crossing capacity entry (Tier 3a gas
 * enrichment, 2026-06). One row of the EIA State-to-State pipeline
 * capacity workbook, surfaced verbatim. STATE-LEVEL CONTEXT only — a
 * border-corridor capacity is NOT a site-deliverable capacity and NOT
 * firm capacity available to a new shipper (see `FuelSupplyEvidence`).
 */
export interface FuelSupplyBorderCapacity {
  /** Pipeline operator name as the EIA workbook spells it. */
  pipeline: string;
  /** 2-letter state code (or non-US label, e.g. Canada) of the From side. */
  fromState: string;
  /** 2-letter state code (or non-US label) of the To side. */
  toState: string;
  /** County on the From side (workbook spelling), when present. */
  fromCounty?: string;
  /** County on the To side (workbook spelling), when present. */
  toCounty?: string;
  /** Corridor capacity, million cubic feet per day. */
  capacityMmcfd: number;
}

/**
 * EIA state-to-state capacity context for the site's state (Tier 3a,
 * 2026-06). NOT a site-deliverable capacity — see the always-on
 * deliverability honesty contract on `FuelSupplyEvidence`. These are the
 * interstate-corridor capacities crossing the site's state border, a
 * coarse "scale + operator presence of the transmission system" signal.
 * `vintageYear` MUST be cited wherever these values are narrated.
 */
export interface FuelSupplyCapacityContext {
  /** Border crossings touching the site's state, descending by capacity. */
  stateBorderCapacitiesMmcfd: FuelSupplyBorderCapacity[];
  /** Close-of-data year of the EIA workbook edition. */
  vintageYear: number;
  /** Provenance basis string. */
  basis: 'eia-state-to-state';
}

/**
 * Curated tariff gas-quality specs for the nearest interstate operator
 * (Tier 3b, 2026-06). Each numeric field is omitted (NOT zero) when the
 * source tariff didn't state it. SCREENING-LEVEL: tariff specs change
 * over time and per receipt/delivery zone — the operator's current
 * effective tariff is authoritative. Hand-seeded from a public-domain
 * survey, never scraped (FERC eTariff is Cloudflare-blocked to clients);
 * `docRef` cites the source doc.
 */
export interface FuelSupplyQualitySpecs {
  source: 'tariff-gtc-curated';
  /** Curated display name of the matched pipeline. */
  pipelineName: string;
  /** Minimum higher heating value, Btu per standard cubic foot. */
  hhvMinBtuScf?: number;
  /** Maximum higher heating value, Btu per standard cubic foot. */
  hhvMaxBtuScf?: number;
  /** Maximum carbon dioxide, mol %. */
  co2MaxPct?: number;
  /** Maximum total inerts, mol %. */
  totalInertsMaxPct?: number;
  /** Maximum hydrogen sulfide, grains per 100 standard cubic feet. */
  h2sMaxGrains?: number;
  /** Citation key into the KB's `sourceDocs`. */
  docRef: string;
}

export type FuelSupplyVerificationStatus =
  | 'verified'
  | 'indicative-only'
  | 'unverified'
  | 'contradictory-superseded';

/**
 * Multidimensional parameter provenance for stationary fuel-cell and site
 * fuel-supply evidence (Finding 16). Prevents flattening origin authority,
 * verification method, and site applicability into a single enum where a
 * site-uploaded brochure could be mistaken for independent SKU verification.
 */
export interface FuelCellParameterProvenance {
  parameterKey?: string;
  originAuthority:
    | 'oem-published'
    | 'supplier-quote'
    | 'government-handbook'
    | 'independent-test-lab'
    | 'utility-or-ahj'
    | 'analyst-assumption';
  verificationMethod:
    | 'unverified-claim'
    | 'third-party-certified'
    | 'site-measured-or-contracted'
    | 'screening-benchmark';
  siteApplicability:
    | 'generic-reference'
    | 'sku-specific-iso-rating'
    | 'site-conditioned-engineering';
  citation: {
    documentIdOrUrl: string;
    title: string;
    publicationOrRevisionDate: string;
    sha256?: string;
    pageOrSection: string;
    exactClaimExcerpt: string;
    unitsAndConditions: string;
  };
  /** Explicit retention status when multiple documents provide conflicting or superseded values. */
  conflictStatus?: 'active-authoritative' | 'contradictory-retained' | 'superseded-retained';
  supersedesDocumentIdOrUrl?: string;
  recordedValueSummary?: string;
}

/**
 * Hydrogen purity standard specification separated from fuel identity (Finding 9).
 * Under ISO 14687:2025, Grade D applies to PEM road-vehicle applications, while
 * Grade E (Categories 1–3) applies to stationary fuel-cell applications.
 */
export interface HydrogenPuritySpec {
  standard: 'ISO-14687' | 'SAE-J2719' | 'oem-custom';
  edition: '2025' | '2019' | string;
  gradeOrCategory: 'Grade-D' | 'Grade-E-Cat1' | 'Grade-E-Cat2' | 'Grade-E-Cat3' | string;
  maxCarbonMonoxidePpmv?: number;
  maxTotalSulfurPpmv?: number;
  maxAmmoniaPpmv?: number;
}

export interface FuelDeliveryPressureEvidence {
  minGuaranteedPsigOrInWc: number | null;
  unit: 'psig' | 'in-wc';
  verificationStatus: FuelSupplyVerificationStatus;
  sourceDocId?: string;
  provenance?: FuelCellParameterProvenance;
}

export interface FuelContractedTransportEvidence {
  serviceType: 'firm-transportation' | 'interruptible' | 'unverified';
  maxDailyQuantityDth?: number | null;
  winterCurtailmentPriority?: string;
  /** Verified site-delivered LDC/transport tariff ($/MMBtu), distinct from wholesale Henry Hub spot. */
  deliveredTariffUsdPerMmbtu?: number | null;
  verificationStatus: FuelSupplyVerificationStatus;
  sourceDocId?: string;
  provenance?: FuelCellParameterProvenance;
}

export interface FuelGasQualityProfileEvidence {
  hhvBtuPerScf?: number | null;
  hhvToLhvRatio?: number | null;
  totalSulfurPpmvMax?: number | null;
  siloxanesPpmvMax?: number | null;
  desulfurizationRequired?: boolean;
  verificationStatus: FuelSupplyVerificationStatus;
  sourceDocId?: string;
  provenance?: FuelCellParameterProvenance;
}

export interface FuelHydrogenSupplyEvidence {
  deliveryMode:
    | 'pipeline'
    | 'compressed-tube-trailer'
    | 'liquid-tanker'
    | 'on-site-electrolyzer'
    | 'unverified';
  purityStandard?: HydrogenPuritySpec;
  storageInventoryLb?: number | null;
  verificationStatus: FuelSupplyVerificationStatus;
  sourceDocId?: string;
  provenance?: FuelCellParameterProvenance;
}

/**
 * Fuel-supply evidence (v1.2.1 HR-5 P2-4) — natural-gas price + pipeline
 * proximity grounding the gas-BTM fuel cost.
 *
 * The natural-gas price is provider-grounded (EIA state citygate / industrial,
 * else Henry Hub national benchmark); `priceSeries` keeps the locality claim
 * honest. Pipeline proximity is READ from `onSiteInfrastructure` (KMZ), not
 * re-derived. `fuelCostPerMwhUsd` = `naturalGasPriceUsdPerMmbtu` ×
 * (`heatRateBtuPerKwh` / 1000) — the heat rate is an ASSUMPTION unless a genset
 * is declared (flagged via `fuelCostBasis` + the surfaced heat rate, per
 * CLAUDE.md rule 5). ADDITIVE + optional on the SED.
 */
export interface FuelSupplyEvidence {
  /** Resolved local price (citygate / industrial) else Henry Hub, $/MMBtu. */
  naturalGasPriceUsdPerMmbtu: number | null;
  priceSeries: 'citygate' | 'industrial' | 'henry-hub-fallback' | 'unavailable';
  state: string | null;
  period: string | null;
  henryHubSpotUsdPerMmbtu: number | null;
  // Pipeline proximity — READ from onSiteInfrastructure (KMZ), not re-derived.
  nearestPipelineKm: number | null;
  nearestPipelineOperator: string | null;
  pipelineSource: 'kmz' | 'document' | 'authored' | 'site-evidence' | 'none';
  // Grounded fuel cost feeding economics.
  /** = priceUsdPerMmbtu × heatRateBtuPerKwh / 1000. Null when price unavailable. */
  fuelCostPerMwhUsd: number | null;
  /** Assumed / declared genset heat rate (Btu/kWh) — flagged as estimate. */
  heatRateBtuPerKwh: number | null;
  fuelCostBasis: 'provider-grounded' | 'unavailable';
  /**
   * Tier 1 gas-infra proximity — READ from `onSiteInfrastructure` (mirrors
   * `nearestPipelineKm`, not re-derived). Optional + additive; absent when
   * the site has no gas-infrastructure layers.
   */
  gasInfraProximity?: GasInfraProximity;
  /**
   * Tier 2 (2026-06) — the nearest pipeline operator's PHMSA-reported
   * transmission system in this state (pure local join off
   * `nearestPipelineOperator`; see `OperatorSystemStats`). Present whenever
   * a nearest-pipeline operator exists: `matchBasis: 'none'` with all-null
   * stats records an honest non-match (narration states the absence).
   * Absent only when there is no nearest-pipeline operator at all.
   */
  operatorSystemStats?: OperatorSystemStats;
  /**
   * Tier 3a (2026-06) — EIA state-to-state interstate-corridor capacity
   * context for the site's state (zero-network lookup over the bundled
   * `eiaStateToStateCapacity.json`; see `eiaPipelineCapacity.ts`).
   * STATE-LEVEL CONTEXT only — never a site-deliverable / firm capacity.
   * Absent when the site has no resolvable US state, or the state has no
   * interstate crossings in the workbook (the deliverability honesty line
   * still narrates the absence).
   */
  capacityContext?: FuelSupplyCapacityContext;
  /**
   * Tier 3b (2026-06) — curated FERC-tariff gas-quality specs for the
   * nearest pipeline operator (alias match over the bundled
   * `gasTariffQualitySpecs.json`; see `gasTariffQuality.ts`).
   * SCREENING-LEVEL ONLY — the operator's current effective tariff is
   * authoritative. Absent when no operator label matches the curated KB
   * (never fabricated).
   */
  qualitySpecs?: FuelSupplyQualitySpecs;
  /**
   * Field-scoped delivery pressure evidence (Finding 1). A general LDC will-serve
   * letter does not automatically verify minimum guaranteed inlet pressure.
   */
  deliveryPressure?: FuelDeliveryPressureEvidence;
  /**
   * Field-scoped contracted firm transportation & delivered tariff evidence (Finding 1).
   */
  contractedTransport?: FuelContractedTransportEvidence;
  /**
   * Field-scoped gas constituent quality & sulfur/siloxane profile (Finding 1).
   */
  gasQualityProfile?: FuelGasQualityProfileEvidence;
  /**
   * Field-scoped hydrogen delivery & ISO 14687:2025 purity specification (Finding 9).
   */
  hydrogenSupply?: FuelHydrogenSupplyEvidence;
  /**
   * Multi-source provenance ledger retaining active, contradictory, and superseded
   * fuel-supply claims without silent overwrite (Finding 16 / Negative Test 8).
   */
  provenanceLedger?: readonly FuelCellParameterProvenance[];
}

/** Nuclear infrastructure evidence. */
export interface NuclearInfraEvidence {
  nearestReactorName: string;
  nearestReactorDistKm: number;
  /**
   * @deprecated R-29: misleading name — this is daily power percentage alias
   * (0-100%), NOT MW nameplate. Kept for back-compat with reports written
   * before R-29. New readers should use `dailyPower`
   * (dated observation) and `nearestReactorNameplateMw` (actual MW).
   */
  nearestReactorPower: number;
  /** Deprecated daily power percentage alias (0-100%). -1 = unknown. Not annual capacity factor. */
  nearestReactorCapacityFactorPct?: number;
  dailyPower?: NrcDailyPowerSummary;
  powerStatusFeed?: { latestReportDate: string | null; invalidRows: number; unmappedUnits: string[]; provenance?: import('../lib/nrcNationalFeed').NrcNationalFeed['provenance'] };
  /** Site-aggregated static catalog nameplate capacity in MW. Static legacy catalog. */
  nearestReactorNameplateMw?: number;
  reactorsWithin200km: number;
  reactorList: {
    name: string;
    distKm: number;
    /** @deprecated R-29: daily power percentage alias, not capacity factor or MW. */
    power: number;
    /** Deprecated daily power percentage alias 0-100%, -1 = unknown. Static legacy catalog. */
    capacityFactorPct?: number;
    dailyPower?: NrcDailyPowerSummary;
    /** Site-aggregated static catalog nameplate MW. Static legacy catalog. */
    nameplateMw?: number;
    /** Number of units aggregated at this site. Static legacy catalog. */
    units?: number;
  }[];
  nuclearInfraMaturity: 'High' | 'Moderate' | 'Low';
}

/** Solar resource evidence. */
export interface SolarEvidence {
  ghi: number;              // kWh/m²/day
  dni: number;              // kWh/m²/day
}

/** Wind resource evidence. */
export interface WindEvidence {
  windSpeed100m: number;    // m/s
  windSpeed80m: number;     // m/s
  capacityFactor: number;   // 0-1
}

/** Electricity pricing evidence. */
export interface ElectricityEvidence {
  retailRate: number;       // ¢/kWh
  state: string;
  year: number;
  basis?: 'state-industrial-average';
  sector?: 'IND';
  units?: 'cents per kilowatt-hour';
}

/** Air quality evidence. */
export interface AirQualityEvidence {
  aqi: number;
  category: string;
  primaryPollutant: string;
  pm25Conc?: number;        // µg/m³
}

/** Connectivity evidence. */
export interface ConnectivityEvidence {
  nearestIxName: string;
  nearestIxDistKm: number;
  ixCount50km: number;
  facilityCount50km: number;
  /**
   * CONNECTIVITY-ADVISORY — whether `nearestIx*` describe a true internet
   * exchange or PeeringDB's nearest-colo-facility fallback (its IX list can
   * fail independently of the facility list; see the `usedIx` branch in
   * `peeringDb.ts`). A 'colo-fallback' reading is display-only; the scorer
   * excludes it as missing data. `undefined` on legacy payloads ⇒ 'ixp'.
   */
  nearestIxKind?: 'ixp' | 'colo-fallback';
  /**
   * KSI — distance (km) from the site centroid to the closest fiber-
   * corridor polyline harvested out of the user's KMZ overlays. Always
   * the MIN across every fiber layer (so a re-uploaded KMZ can only
   * tighten the distance, never inflate it). `undefined` when the site
   * has no fiber KMZ layers and no Live Literature replacement landed.
   *
   * Provenance flag on the wrapping `EvidenceSection` is stamped
   * `'doc'` (or `'provider+doc'` when a provider value already lived in
   * the slot) with `provenanceDetail.mergeReason = 'kmz-import'`, so
   * the Score Provenance Panel + lineage trail surface the KMZ source.
   */
  nearestFiberKm?: number;
  /**
   * Source-stated last-mile fiber construction distance to a service
   * handoff, in feet. Planned/documented build length is separate from
   * centroid-to-corridor proximity, a full KMZ route, and IX distance.
   * Missing is unknown; a value does not confirm service or diversity.
   */
  fiberBuildDistanceFt?: number;
  /**
   * KSI — the KMZ layer name of the closest fiber corridor (e.g. the
   * "Fiber – AT&T 2024" the user typed when drawing the layer). Free-
   * form; renderers fall back to "Unknown carrier" when undefined.
   */
  fiberProvider?: string;
  /**
   * KSI — the `SiteDocument.id` of the KMZ that produced the layer.
   * Optional today (KSD8 is what makes per-layer doc-id provenance
   * authoritative); the bootstrap best-effort-passes the id list of
   * KMZ-typed SiteDocuments for now, so this may be the first doc id
   * in that list rather than the actual contributing KMZ until KSD8
   * lands.
   */
  fiberLayerSourceId?: string;
  /** KSI — epoch-ms of the bootstrap pass that populated the slot. */
  fiberRetrievedAt?: number;
}

/** Parcel/land evidence. */
export interface ParcelEvidence {
  zoning: string;
  landUse: string;
  owner?: string;
  acres?: number;
}

export type JurisdictionScreeningStatus =
  | 'inside-city-limits'
  | 'inside-etj'
  | 'outside-known-etj'
  | 'mixed'
  | 'unknown';

export interface JurisdictionOverlapEvidence {
  name: string;
  kind: 'city-limit' | 'etj';
  acres: number;
  fraction: number;
  sourceId: string;
}

export interface ParcelJurisdictionEvidence {
  parcelId: string;
  parcelAcres: number;
  overlaps: JurisdictionOverlapEvidence[];
  status: JurisdictionScreeningStatus;
}

export interface JurisdictionRegistryApplicabilityEvidence {
  status: 'not-required' | 'verified' | 'mismatch' | 'cross-county' | 'unavailable';
  method: 'fcc-census-block+us-census-county-polygon';
  checkedAt: string;
  queryPoint: { lat: number; lng: number };
  expectedRegistry?: {
    registryId: string;
    stateFips: string;
    countyFips: string;
  };
  fcc: {
    status: 'not-run' | 'verified' | 'mismatch' | 'unavailable';
    sourceUrl: string;
    blockFips?: string;
    blockBbox?: [number, number, number, number];
    stateFips?: string;
    countyFips?: string;
    countyName?: string;
    stateCode?: string;
    error?: string;
  };
  countyContainment: {
    status: 'not-run' | 'verified' | 'not-intersecting' | 'cross-county' | 'unavailable';
    sourceUrl: string;
    geoid?: string;
    outsideParcelIds?: string[];
    error?: string;
  };
}

/**
 * Geographic screening only. `authorityScope` remains not-evaluated until a
 * current ordinance, permit opinion, or other legal evidence is supplied.
 */
export interface JurisdictionEvidence {
  status: JurisdictionScreeningStatus;
  /** Compatibility alias for the source-grade foundation schema. */
  classification?: JurisdictionScreeningStatus;
  insideCityLimits: boolean | null;
  insideEtj: boolean | null;
  municipalities: string[];
  etjAuthorities: string[];
  governingJurisdiction?: string | null;
  incorporatedPlace?: { name: string; geoid?: string };
  candidates?: string[];
  authorityScope: 'not-evaluated';
  authorityNotes?: string;
  registryId: string;
  /** Persisted proof of why a local registry was selected or rejected. */
  registryApplicability?: JurisdictionRegistryApplicabilityEvidence;
  stateFips?: string;
  countyFips?: string;
  registryCoverage: 'complete' | 'partial' | 'not-published' | 'fallback' | 'unavailable';
  datasetEffectiveDate?: string;
  lastReviewedAt: string;
  authorityReferences: string[];
  sources: Array<{
    label: string;
    url: string;
    retrievedAt: string;
    datasetEffectiveDate?: string;
    kind?: 'city-limit' | 'etj' | 'census-place' | 'county-boundary' | 'ordinance';
    providerId?: string;
    dataAsOf?: string;
    featureId?: string;
  }>;
  parcels: ParcelJurisdictionEvidence[];
  /** Compatibility alias for legacy readers; new providers write `parcels`. */
  perParcel?: Array<{
    parcelId: string;
    classification: JurisdictionScreeningStatus;
    insideCityLimits: boolean | null;
    insideEtj: boolean | null;
  }>;
  warnings: string[];
}

export type JurisdictionClassification = JurisdictionScreeningStatus;
export type JurisdictionSource = JurisdictionEvidence['sources'][number];

/** Regulatory stance evidence. */
export interface RegulatoryEvidence {
  state: string;
  nuclearStance: 'Favorable' | 'Neutral' | 'Restrictive';
  stateNotes: string;
  /** Additive jurisdiction screen; absent on legacy SEDs. */
  jurisdiction?: JurisdictionEvidence;
  /**
   * DAT-FOLLOWUP-4 — state-resolution provenance. `'bbox-estimate'` means
   * regulatory-db could reach neither the Census TIGER snapshot nor the FCC
   * Census Block API and `state` is `estimateStateFromCoords`' bounding-box
   * guess (wrong at borders, and it reads southern Ontario as NY).
   *
   * Written ONLY for that estimate. A TIGER or FCC lookup writes nothing, so
   * the regulatory section's content hash (Stage 2 evidence basis,
   * decision-analysis currency) does not move for the common case. The
   * price: absence does NOT prove a lookup — it is a lookup on SEDs compiled
   * after this field existed, and unknown on older ones. Never infer an
   * authoritative state from absence (`sedStateOrigin` in
   * `src/lib/inferSiteState.ts` reads it as `'unmarked'`).
   *
   * Qualifies the provider-written `state` only: when a document supplied
   * the current state (`docFieldSources.state`) the marker no longer
   * applies. Additive; absent on every legacy SED.
   */
  stateResolution?: 'bbox-estimate';
}

/** Screening terrain derived from a parcel-clipped regular DEM sample grid. */
export type TopographyEvidence = import('./topography').TopographyEvidence;

/** Public soils screen; professional conclusions remain document-only. */
export interface GeotechnicalEvidence {
  publicScreen?: {
    mukey?: string;
    mapUnitName?: string;
    usdaTexture?: string;
    inferredUsCsClass?: string;
    drainageClass?: string;
    depthToBedrockM?: number | null;
    seasonalWaterTableDepthM?: number | null;
    presumptiveBearingPressureKPa?: number | null;
    bearingPressureConfidence?: 0.45 | 0.6 | null;
    basis?: string;
  };
  professionalFacts?: {
    preparedBy?: string;
    reportDate?: string;
    bearingCapacityKPa?: number;
    soilClassification?: string;
    boreholeDepthM?: number;
    groundwaterDepthM?: number;
    foundationRecommendation?: string;
    settlementRisk?: string;
    liquefactionRisk?: string;
    expansiveSoilRisk?: string;
    seismicSiteClass?: string;
  };
  screeningOnly: boolean;
  warnings: string[];
  professionalConflicts?: ProfessionalEvidenceConflict[];
}

export interface SurveyEvidence {
  standard: 'ALTA/NSPS' | 'boundary' | 'topographic' | 'other';
  standardYear?: '2021' | '2026' | 'other';
  surveyor?: string;
  surveyDate?: string;
  surveyedAcreage?: number;
  legalDescriptionConfirmed?: boolean;
  accessConfirmed?: boolean;
  easements?: string[];
  rightsOfWay?: string[];
  encroachments?: string[];
  utilitiesObserved?: string[];
  tableAItems?: string[];
  floodZone?: string;
  contoursIncluded?: boolean;
  contourIntervalFt?: number;
  verticalDatum?: string;
  notes?: string;
  professionalConflicts?: ProfessionalEvidenceConflict[];
}

export interface MineralResourcesEvidence {
  publicScreen?: {
    /** Null means no parcel boundary was available; it is not a false zero. */
    occurrenceCountOnParcel: number | null;
    occurrenceCountWithin1Mi: number;
    occurrenceCountWithin5Mi: number;
    nearestDistanceMi: number | null;
    commodities: string[];
    occurrences?: Array<{
      featureId: string;
      name: string;
      developmentStatus: string | null;
      distanceMi: number;
      onParcel: boolean | null;
      commodities: string[];
      sourceUrl?: string;
    }>;
    queriedFeatureCount?: number;
    truncated: boolean;
    sourceVintage?: string;
    screeningOnly?: true;
  };
  titleFacts?: {
    preparer?: string;
    reportDate?: string;
    countyRecordReference?: string;
    ownershipStatus?: 'unified' | 'severed' | 'reserved' | 'leased' | 'unknown';
    reservedCommodities?: string[];
    activeLeases?: string[];
    surfaceUseRestrictions?: string[];
  };
  warnings: string[];
  professionalConflicts?: ProfessionalEvidenceConflict[];
}

export interface CulturalResourcesEvidence {
  publicRegistryScreen?: {
    /** Null means no parcel boundary was available; it is not a false zero. */
    listedPropertiesOnParcel: number | null;
    listedPropertiesWithin1Mi: number;
    listedPropertiesWithin5Mi: number;
    nearestDistanceMi: number | null;
    listedProperties?: Array<{
      featureId: string;
      name: string;
      resourceType: string | null;
      listingStatus: string | null;
      certificationDate: string | null;
      distanceMi: number;
      onParcel: boolean | null;
    }>;
    queriedFeatureCount?: number;
    truncated: boolean;
    sourceVintage?: string;
    screeningOnly?: true;
    preciseLocationsWithheld?: true;
  };
  professionalSurvey?: {
    qualifiedProfessional?: string;
    surveyDate?: string;
    surveyLevel?: string;
    acreageSurveyed?: number;
    resourcesIdentified?: boolean;
    resourceCount?: number;
    eligibilityRecommendation?: string;
    effectFinding?: string;
    shpoCaseNumber?: string;
    sensitiveLocationWithheld: true;
  };
  warnings: string[];
  professionalConflicts?: ProfessionalEvidenceConflict[];
}

/** NRC ADAMS document reference. */
export interface AdamsDocEvidence {
  accessionNumber: string;
  title: string;
  date: string;
  documentType: string;
}

/** Land/site profile from user boundary.
 *
 * EL2: `elevationMin/Max/Avg` widened from `number` to `number | null`.
 * Previously these defaulted to `0` whenever an elevation lookup failed
 * (network error, empty results, malformed response, no boundary
 * drawn) which made "real coastal 0m" indistinguishable from "lookup
 * unavailable" and caused the Sickel/Redhead reports to publish `0m
 * ASL` with full confidence.
 *
 * `null` means "elevation unavailable — do not synthesise a number".
 * Every consumer goes through `formatElevation(value)` which renders
 * `"Data unavailable"` for null, `"<n> m ASL"` for a real metres value.
 *
 * Units: metres above sea level (ASL). Provider boundary
 * (CoolProp-equivalent for spatial data) sits in the elevation provider
 * — every API converts to metres before populating these fields.
 */
export interface SiteProfileEvidence {
  elevationSourceQualification?: import('./elevationSourceQualification').ElevationSourceQualification;
  acres: number | null;
  perimeterKm: number | null;
  elevationMin: number | null;
  elevationMax: number | null;
  elevationAvg: number | null;
  /**
   * EL3 — population standard deviation across the sampled elevation
   * points (metres ASL). Distinguishes a flat 5-point sample (small
   * stdev → real flat parcel) from a 5-point sample where one fell in
   * a drainage ditch (large stdev → outlier dragging avg down).
   *
   * `null` when elevation is unavailable OR fewer than 2 points were
   * sampled (legacy single-point provider path). Optional on the type
   * for back-compat with SEDs written before EL3 landed.
   */
  elevationStdev?: number | null;
  /**
   * EL3 — number of elevation sample points that returned a usable
   * value. Renderers + LLM prompts surface this when they want to
   * flag a degraded sample ("3 of 5 points returned"). Optional for
   * back-compat with pre-EL3 SEDs.
   */
  elevationPointsSampled?: number;
  slopePercent: number;
  /**
   * Distinguishes a real local terrain gradient from the legacy
   * elevation-relief/characteristic-length proxy. Optional on legacy SEDs.
   */
  slopeMethod?: 'topography-grid' | 'relief-proxy' | 'unavailable';
  /** Provider/method label for a topography-grid slope. */
  slopeSource?: string;
  /**
   * EX-1 (EAS-FOLLOWUP-NREL-SOLAR) — polygon-grid solar resource
   * survey. Populated when the producer ran the polygon sampler
   * (`services/dataProviders/solar/polygonSolarSampler.ts`). Carries
   * GHI / DNI / DHI at the centroid PLUS per-component avg/stdev/
   * min/max across an in-polygon 3×3 grid (cap = SOLAR_MAX_SAMPLES =
   * 9). Optional and additive: legacy SEDs and the centroid-fallback
   * path leave this `undefined` and downstream consumers fall back to
   * the existing `solar` evidence section.
   *
   * Stamped on `siteProfile` (NOT on `solar`) so the per-polygon
   * survey lives next to acres/perimeter/elevation — every "boundary-
   * sampled" fact in one place; the top-level `solar` section keeps
   * its centroid-only contract for back-compat.
   *
   * Shape mirrors `PolygonSolarResource` in the sampler module. We
   * inline the structural type rather than importing it so this SED
   * schema doesn't pull in provider-specific code (same pattern as
   * `attemptStages` above).
   */
  solarResource?: {
    pointsSampled: number;
    pointsRequested: number;
    centroid: { lat: number; lng: number; ghi: number; dni: number; dhi: number | null };
    ghi: { avg: number; stdev: number | null; min: number; max: number };
    dni: { avg: number; stdev: number | null; min: number; max: number };
    dhi: { avg: number; stdev: number | null; min: number; max: number } | null;
  };
}

/**
 * Format an elevation value for display.
 *
 * EL2 guard helper — every render site that touches `elevationAvg /
 * Min / Max` must route through this so `null` (lookup unavailable)
 * renders as `"Data unavailable"` instead of silently coercing to
 * `"0 m ASL"`. Non-finite values (`NaN`, `Infinity`) are treated as
 * unavailable too — defence-in-depth against legacy SEDs that wrote
 * `NaN` instead of `null`.
 *
 * @param meters Elevation in metres ASL, or null when unavailable.
 * @returns `"Data unavailable"` for null/non-finite, otherwise
 *   `"<rounded> m ASL"`. The "ASL" suffix matches the existing report
 *   wording (`isFinite(elev) ? elev.toFixed(0) + 'm ASL'` …).
 */
export function formatElevation(meters: number | null | undefined): string {
  if (meters == null || !Number.isFinite(meters)) return 'Data unavailable';
  return `${Math.round(meters)} m ASL`;
}

/**
 * The complete Site Evidence Document.
 */
/**
 * Canonical list of SED section ids — one per evidence-section field on
 * SiteEvidenceDocument. Exported as a const tuple so the relevance
 * classifier (Documents_On_Site Prompt 2) can score uploaded docs
 * against the same surface the rest of the app uses. If you add or
 * rename a section, update this list AND the SiteEvidenceDocument
 * fields below in lockstep.
 */
export const SED_SECTION_IDS = [
  'climate',
  'waterStress',
  'seismic',
  'grid',
  'flood',
  'environmental',
  'nuclearInfra',
  'solar',
  'wind',
  'electricity',
  'airQuality',
  'connectivity',
  'parcel',
  'regulatory',
  'siteResearch',
  'adamsDocuments',
  'siteProfile',
  // Economic/workforce evidence — canonical public profile, deterministic
  // screening outputs, and policy-valid comparable-project research frozen at
  // SED compile time. Held/excluded research is retained as counts only.
  'economicEvidence',
  // KSD1 — structured KMZ-derived facts (substations, fiber corridors,
  // water features, named points). Populated by `compileOnSiteInfrastructure`
  // (KSD2) from `site.siteLayers` after the main `compileSiteEvidence`
  // pass. Status is `'verified-from-kmz'` when at least one layer fed
  // a field; `'unavailable'` when the site has no KMZ layers.
  'onSiteInfrastructure',
  // LL1 (Live Literature Retrieval) — externally retrieved peer-reviewed +
  // preprint sources from Crossref, arXiv, and PubMed. Populated by the
  // `external-literature` synthetic provider (orchestrator in
  // `src/services/externalLiteratureOrchestrator.ts`). Section payload is
  // `ExternalLiteratureSection` (declared below). The peer-reviewed
  // `peerReviewDb` curated knowledge base stays as the spine; live
  // literature is the current-research complement on top.
  'externalLiterature',
  // PSE-1 (Public Sentiment Engine) — community + regulatory + media
  // sentiment per BTM technology. Populated by the
  // `public-sentiment` synthetic provider (orchestrator in
  // `src/services/publicSentimentOrchestrator.ts`) which fans out to
  // GDELT 2.0 (media tone), MuniCode/Granicus/Civic-Plus (council
  // minutes), and Regulations.gov/FERC eLibrary/NRC ADAMS (docket
  // activity). Section payload is `PublicSentimentSection` (declared
  // below). PSE-1 ships the orchestrator skeleton + types + scoring
  // axis; the three source fetchers themselves are stubbed (return
  // `[]`) and land in PSE-2 / PSE-3.
  'publicSentiment',
  /** Project Intent — reactor design class (intent-stamped, no live provider). */
  'technology',
  // v1.2.1 HR-5 P2-2 / P2-3 / P2-4 — first-class BTM grounding sections.
  // `cooling` / `power` / `firmCapacity` / `lcoe` are intent-stamped; `fuelSupply`
  // is provider-grounded (EIA natural gas) + KMZ pipeline proximity. All ADDITIVE.
  'cooling',
  'power',
  'firmCapacity',
  'lcoe',
  'fuelSupply',
  // SED D2 — typed Interconnection + Incentives evidence sections (replace
  // ungrounded AI narrative). `interconnection` grounds power-availability
  // (ISO/RTO queue position via the deterministic iso-queue provider +
  // declared `ProjectIntent.interconnectionStatus`); `incentives` is the
  // pure IRA tax-credit calculator off `financialAssumptions`. Both ADDITIVE.
  'interconnection',
  'incentives',
  // SED D2 (H-6) — second D2 slice: typed multi-hazard + transmission-headroom
  // + water-resource evidence sections (replace ungrounded AI narrative).
  // `multiHazard` is PROVIDER-GROUNDED (FEMA National Risk Index county
  // FeatureServer — wildfire / strong-wind / hail / drought + composite, beyond
  // the existing flood + seismic sections). `transmissionHeadroom` +
  // `waterResource` are typed sections with HONEST-unavailable providers (no
  // clean point-queryable public API in-repo — figures NEVER fabricated; real
  // sources named as follow-up). All ADDITIVE + optional.
  'multiHazard',
  'transmissionHeadroom',
  'waterResource',
  'topography',
  'geotechnical',
  'survey',
  'mineralResources',
  'culturalResources',
] as const;
export type SedSectionId = typeof SED_SECTION_IDS[number];

/**
 * KSD1 — supplemental `EvidenceStatus` for `onSiteInfrastructure`.
 *
 * Kept separate from the main `EvidenceStatus` union so legacy
 * consumers (renderers, classifiers, formatters) that switch on the
 * existing statuses keep compiling even before they know about
 * `'verified-from-kmz'`. Add this to the main union once every reader
 * is verified to handle it.
 */
export type OnSiteInfrastructureStatus = EvidenceStatus | 'verified-from-kmz';

/* ── KSD1 — On-site infrastructure (KMZ-derived) ─────────────────── */

export interface OnSiteSubstation {
  /** KMZ pin label (e.g. "Entergy 230kV Substation"). */
  label: string;
  coords: { lat: number; lng: number };
  /** Distance from the site centroid (km), Haversine great-circle. */
  distanceKm: number;
  /**
   * Voltage parsed from the label when the pattern `(\d+)kV` matches.
   * Null when the label doesn't carry a voltage stamp (the user can
   * still type one into the SED amendment surface).
   */
  voltageKv?: number | null;
  /** Owner inferred from the label (free-form). */
  ownerKnown?: string | null;
}

export interface OnSiteCorridor {
  label: string;
  /**
   * Inferred from `SiteLayerCategory`:
   *  - `infrastructure.fiber`     → 'fiber'
   *  - `infrastructure.electrical`→ 'transmission'
   *  - `infrastructure.pipeline`  → 'pipeline'
   *  - `infrastructure.transport` → 'road' (or 'rail' if label says so)
   *  - anything else              → 'unknown'
   */
  kind: 'fiber' | 'transmission' | 'pipeline' | 'rail' | 'road' | 'unknown';
  /** True when the polyline crosses the parcel envelope or sits within ~25 m of it. */
  crossesParcel: boolean;
  /** Closest distance from the polyline to the parcel boundary (km). */
  distanceFromParcelKm: number;
  /** Total polyline length (km). Optional for legacy points-only entries. */
  lengthKm?: number;
  /**
   * KSI — closest distance (km) from the site centroid to any vertex
   * on the polyline. Drives `crossBindAllKmzCorridors` so the cross-
   * bind helper can pick the closest corridor of each kind without
   * having to re-walk the geometry. Optional for back-compat with
   * SEDs compiled before KSI.
   */
  distanceFromCentroidKm?: number;
  /**
   * KSI — `SiteDocument.id` of the KMZ that contributed this corridor.
   * Best-effort today (KSD8 lands per-layer doc-id provenance); the
   * compiler stamps the first KMZ doc id from the
   * `sourceKmzDocIds` array passed in by the bootstrap. Surfaces in
   * PRV / EVL as "Sourced from KMZ: <fileName>" with click → KMZ doc
   * opens. Optional for back-compat.
   */
  sourceKmzDocId?: string;
  /** Optional operational throughput if sourced from pipeline metadata. */
  massFlow?: { value: number; unit: string; basis?: string };
  /** Optional composition map (key/value percentages or fractions). */
  gasComposition?: Record<string, number>;
  /** Optional availability percentage from provider/API enrichment. */
  availabilityPct?: number;
}

export interface OnSiteWaterFeature {
  label: string;
  /**
   * Inferred from the label / category. Defaults to `'unknown'` when
   * the KMZ name doesn't tip its hand.
   */
  kind: 'pond' | 'stream' | 'wetland' | 'reservoir' | 'unknown';
  /** True when the polygon is fully inside the parcel envelope. */
  withinParcel: boolean;
  /** Distance from the site centroid to the feature centroid (km). */
  distanceKm: number;
}

export interface OnSiteOtherPoint {
  label: string;
  coords: { lat: number; lng: number };
  distanceKm: number;
}

/** Structured KMZ-derived `onSiteInfrastructure` SED section payload. */
export interface OnSiteInfrastructureData {
  substations: OnSiteSubstation[];
  corridors: OnSiteCorridor[];
  waterFeatures: OnSiteWaterFeature[];
  otherPoints: OnSiteOtherPoint[];
  /**
   * Doc ids of the KMZ files that contributed layers. Today the
   * platform doesn't persist a KMZ-doc reference per-layer; this is
   * populated when the parser surfaces it (KSD8 lands the per-layer
   * provenance). Empty array is the legacy/no-provenance case.
   */
  sourceKmzDocIds: string[];
  /** Number of `siteLayers` rows the compiler consumed. */
  layerCount: number;
  /** Public/modelled layers retained only as lower-tier screening. */
  screeningLayerCount?: number;
  /** ISO timestamp of the compile. */
  parsedAt: string;
  /**
   * KSI — closest pipeline KMZ corridor distance (km) summarised at
   * the section level. No dedicated `gas` SED section exists today
   * (the BTM-bridge gas analysis lives inside `siteResearch`), so the
   * KMZ-derived pipeline proximity lands here as an aggregate that
   * downstream surfaces can read directly. Optional — undefined when
   * the site has no pipeline KMZ layers.
   */
  nearestPipelineKm?: number;
  /** KSI — label/operator of the closest pipeline corridor. */
  nearestPipelineOperator?: string;
  /** Closest pipeline operational throughput (if available). */
  nearestPipelineMassFlow?: { value: number; unit: string; basis?: string };
  /** Scalar alias for extraction/edit flows. */
  nearestPipelineMassFlowValue?: number;
  /** Scalar alias for extraction/edit flows. */
  nearestPipelineMassFlowUnit?: string;
  /** Scalar alias for extraction/edit flows. */
  nearestPipelineMassFlowBasis?: string;
  /** Closest pipeline availability (if available). */
  nearestPipelineAvailabilityPct?: number;
  /** Closest pipeline composition snapshot (if available). */
  nearestPipelineGasComposition?: Record<string, number>;
  /** Flattened summary for variable browser + coverage chips. */
  nearestPipelineGasCompositionSummary?: string;
  /**
   * Tier 1 gas-infra proximity aggregate (compressor stations /
   * interconnects / receipt points from the EIA/HIFLD ArcGIS catalog
   * layers). Compiled here (next to `nearestPipelineKm`) because no
   * dedicated `gas` SED section exists; `buildFuelSupplyEvidence` READS
   * this — never re-derives. Optional — undefined when the site has no
   * gas-infrastructure point layers.
   */
  gasInfraProximity?: GasInfraProximity;
  gasInfraProximityEvidenceUse?: 'site-truth' | 'screening';
}

/** SCM v2 — procurement snapshot stamped at supply-chain-risk report generation. */
export interface SupplyChainSnapshotEvidence {
  /** Owner-declared excluded/unpriced scope; public-safe text, no private source IDs. */
  materialScopeDisclosure?: string;
  bomItemCount: number;
  supplierCount: number;
  quoteCount: number;
  quoteCoveragePct: number;
  partnerSupplierCount: number;
  comparableQuoteLineCount: number;
  bomCapexTotalUsd: number;
  capexVariancePct: number | null;
  criticalPathItemCount: number;
  longestLeadMonths: number | null;
  topRiskCategories: string | null;
  topCriticalItems: {
    bomItemId: string;
    name: string;
    leadTimeMonthsHigh: number;
    /**
     * Months of ordering slack vs the COD target. `null` = no order-by
     * constraint could be computed (criticalPathAnalysis models that as
     * Infinity, which JSON persistence turns into null — persisted SEDs
     * carry null, so readers MUST guard).
     */
    slackMonths: number | null;
  }[];
  categoryBreakdown: { category: string; count: number; totalCostUsd: number }[];
  insightSummary: string;
}

/** Version of the economic-evidence envelope frozen into a SED. */
export const SED_ECONOMIC_EVIDENCE_SCHEMA_VERSION = 1 as const;

/**
 * Report-safe projection of a completed economic research run.
 *
 * Only policy-valid comparable records cross into the factual payload. Held
 * and excluded candidates remain counts so report generation can disclose
 * coverage without accidentally treating rejected research as evidence.
 */
export interface SedEconomicResearchEvidence {
  runId: string;
  resultSnapshotId: string;
  siteFingerprint: string;
  generatedAt: string;
  policyVersion: string;
  sourceDefinitionVersion: string;
  objective?: import('./economicResearch').EconomicResearchObjective;
  facetCoverage?: Partial<import('./economicResearch').EconomicResearchFacetCoverage>;
  policyValidRecords: readonly import('./economicResearch').EconomicResearchComparableSummary[];
  comparableAggregates?: readonly import('./economicResearch').EconomicResearchComparableAggregate[];
  sourceFailures: readonly import('./economicResearch').EconomicResearchSourceFailure[];
  basisCounts: import('./economicResearch').EconomicResearchResultSnapshot['basisCounts'];
  fiscalBaselineDrafts: readonly import('./economicResearch').FiscalBaselineDraft[];
  nonPromoted: {
    heldCount: number;
    excludedCount: number;
  };
}

/**
 * Full economic/workforce evidence frozen by an SED rebuild. `sourceRevision`
 * is derived from the three canonical source snapshot ids and drives freshness;
 * `contentHash` covers the complete, non-volatile payload for audit/debugging.
 */
export interface EconomicEvidenceData {
  schemaVersion: typeof SED_ECONOMIC_EVIDENCE_SCHEMA_VERSION;
  sourceRevision: string;
  contentHash: string;
  capturedAt: string;
  workforceProfile?: import('./economicContribution').EconomicProfileSnapshot;
  /** Deterministic research-backed water screen, projected independently from jobs. */
  waterUseScreening?: import('./economicContribution').WaterUseScreeningResult;
  /** Phase and alternate MW-boundary cases; no staffing data in this water facet. */
  waterUseScenarioScreens?: readonly {
    scenarioId: string;
    label: string;
    assumption: string;
    result: import('./economicContribution').WaterUseScreeningResult;
  }[];
  waterScreeningSources?: readonly import('./economicContribution').EconomicSourceRef[];
  screening?: import('./economicContribution').EconomicContributionScreeningSnapshot;
  /** Catalog sources actually referenced by the deterministic screening. */
  screeningSources: readonly import('./economicContribution').EconomicSourceRef[];
  research?: SedEconomicResearchEvidence;
}

/* ── LL1 — Live literature retrieval (Crossref / arXiv / PubMed) ─── */

/**
 * A single literature entry returned by one of the external-literature
 * providers (Crossref, arXiv, PubMed). Shape mirrors what the
 * `externalLiteratureOrchestrator` builds out of each upstream API
 * response, normalised across providers so the downstream renderer +
 * `assembleReferences()` only have to read one type.
 *
 * Field origin notes:
 *  - `id`                — stable hash of (provider + identifier); used
 *    to deduplicate when the same paper surfaces from multiple providers
 *    (e.g. arXiv preprint + Crossref published version).
 *  - `identifier`        — provider-native id: DOI for Crossref, arXiv id
 *    (`2401.12345v2`) for arXiv, PMID for PubMed.
 *  - `url`               — resolvable: `https://doi.org/<doi>` for
 *    crossref/pubmed (when DOI present), `https://arxiv.org/abs/<id>` for
 *    arXiv, `https://pubmed.ncbi.nlm.nih.gov/<pmid>/` as the PubMed
 *    fallback when the entry has no DOI.
 *  - `abstractSnippet`   — plain-text, capped at 280 chars. Always
 *    populated; `abstract` is the full text and may be empty when the
 *    upstream record had none.
 *  - `relevanceScore`    — provider values are provisional; report selection
 *    recomputes a deterministic topical match, never study/site confidence.
 *  - `citationCount`     — Crossref's `is-referenced-by-count`; PubMed +
 *    arXiv leave this undefined.
 */
export interface LiteratureEntry {
  id: string;
  provider: 'crossref' | 'arxiv' | 'pubmed';
  title: string;
  authors: string[];
  year: number;
  journal?: string;
  doi?: string;
  url: string;
  abstract?: string;
  abstractSnippet: string;
  relevanceScore: number;
  topics: string[];
  citationCount?: number;
  identifier: string;
  /** Bibliographic form and recorded integrity signals; neither proves peer review. */
  publication?: {
    kind: 'journal-article' | 'conference-paper' | 'preprint' | 'posted-content' | 'other' | 'unknown';
    status?: 'retracted' | 'withdrawn' | 'notice' | 'expression-of-concern' | 'unknown';
    /** Explicit same-work/version DOI links only; never correction/retraction targets. */
    relatedDois?: string[];
    notices?: Array<{ type: string; doi?: string }>;
    version?: number;
  };
  /** Queries that returned this entry, retained through deduplication. */
  retrievalQueries?: string[];
}

/**
 * Payload of the `externalLiterature` SED section.
 *
 *  - `status: 'ok'`           — at least one provider returned entries.
 *  - `status: 'partial'`      — some providers failed; the entries we have
 *    are still usable. `reason` carries which providers failed.
 *  - `status: 'unavailable'`  — no selected evidence or feature flag is off;
 *    `entries` is empty. `reason` distinguishes failure, no matches and exclusion.
 */
export interface ExternalLiteratureSection {
  status: 'ok' | 'partial' | 'unavailable';
  entries: LiteratureEntry[];
  queriesIssued: string[];
  providersAttempted: ('crossref' | 'arxiv' | 'pubmed')[];
  fetchedAt: string;
  reason?: string;
  /** Bounded selection receipt; relevance is topical matching, not site evidence confidence. */
  selection?: import('../lib/literatureQuality').LiteratureSelectionAudit;
  /**
   * CL-2 (row 0.99286) — epoch-ms timestamp stamped by
   * `orchestrateExternalLiterature` each time the orchestrator actually
   * fans out to the upstream providers. Used by the report renderer to
   * surface a tiny "literature updated" indicator when this stamp is
   * newer than the consuming section's narrative timestamp. Optional
   * for back-compat — legacy SEDs persisted before CL-2 have no field;
   * readers treat absent as "never refreshed since SED compile".
   *
   * Distinct from `fetchedAt` (ISO string) which is the human-readable
   * stamp surfaced in the reference appendix; `lastLiteratureRefreshAt`
   * is the machine-readable comparable used for the freshness check.
   */
  lastLiteratureRefreshAt?: number;
  /**
   * CL-2 — section-level quality warnings emitted by the orchestrator.
   * Today the only warning is `'literature-refreshed'`, set whenever
   * `orchestrateExternalLiterature` returned new data (cache miss /
   * stale refresh) so the renderer can flip a "literature updated"
   * indicator without the section having to re-narrate. Future entries
   * land here; consumers iterate, they do not pattern-match on length.
   */
  qualityWarnings?: ReadonlyArray<'literature-refreshed'>;
}

/* ── PSE-1 — Public Sentiment Engine types ────────────────────────── */

/**
 * Per-BTM technology bucket the sentiment engine aggregates into. Five
 * tech archetypes mirror the datacrate model's BTM (behind-the-meter)
 * decision space. `general-datacenter` is the catch-all for items that
 * speak to data-center siting as a whole (not a specific generation
 * technology) — e.g. council debates over data-center moratoria.
 */
export type PublicSentimentBtmTech =
  | 'nuclear-smr'
  | 'natural-gas'
  | 'solar-bess'
  | 'wind-bess'
  | 'general-datacenter';

/**
 * Source families the PSE-1 orchestrator fans out to. Each is a
 * free-tier public surface (no API key required for read access).
 *
 *  - `gdelt`            — GDELT 2.0 Doc API. Global news + media-tone
 *    signal. Drives `media_tone` and is the highest-volume source.
 *  - `local-minutes`    — MuniCode / Granicus / Civic-Plus council
 *    minutes scraper. Surfaces planning-commission and council debates
 *    in the host municipality. Drives `btm_tech_support` +
 *    `datacenter_attitude`.
 *  - `federal-dockets`  — Regulations.gov / FERC eLibrary / NRC ADAMS
 *    docket activity. Drives `docket_activity` — a frequency-of-filing
 *    signal rather than a tone signal, so its sentimentScore is
 *    typically derived from filing posture (opposed / supportive) when
 *    inferable, else neutral.
 */
export type PublicSentimentSourceId =
  | 'grounded-search'
  | 'uploaded-document'
  | 'gdelt'
  | 'local-minutes'
  | 'federal-dockets'
  // PSR-FOLLOWUP-IIA-INGEST — fourth source: image-observation claims
  // (protest flyers + community-meeting handouts) that IIA-2/3 deposited
  // onto `siteContext.claims[]`. Reaches IN to the user's own evidence
  // base; classified text-only via a Gemini polarity call. Confidence
  // capped at 0.50 (floor of IIA-3's 0.55 vision cap × LLM polarity
  // ceiling). See `imageObservationSentiment.ts`.
  | 'image-observation'
  // PSR-5b/c — commercial sentiment providers, per-tenant-keyed. Only
  // dispatched when `mode === 'standalone-psr'` AND the tenant has
  // provisioned an API key under `tenants/{tenantId}/integrations/{vendor}`.
  // Confidence cap 0.70 (calibrated classifiers — see
  // `brandwatchSentiment.ts` + `talkwalkerSentiment.ts`).
  | 'brandwatch'
  | 'talkwalker';

/**
 * One normalised sentiment observation. Producers (the three source
 * fetchers) emit `PublicSentimentEntry[]`; the orchestrator dedupes
 * and the aggregator collapses them into per-tech buckets.
 *
 * Field origin notes:
 *  - `sentimentScore`     — −1 (opposed) … 0 (neutral) … +1 (supportive).
 *    NaN is not permitted; producers MUST emit a finite score even for
 *    a neutral filing (use `0` with `confidence: 0.4`).
 *  - `confidence`         — 0 … 1. Quality of the source-to-tech
 *    mapping and the tone extraction. Drives the weighted aggregation.
 *  - `supportingSnippet`  — short verbatim excerpt (≤280 chars).
 *    Populated for every entry so the renderer can show provenance
 *    without re-fetching the source.
 *  - `url`                — resolvable link to the original source.
 *  - `fetchedAt`          — ISO timestamp of the underlying fetch.
 */
/**
 * Geographic scope of a sentiment observation. The orchestrator queries
 * the public-discourse sources at three nested levels so a legitimately
 * quiet site (no local media / council coverage — e.g. rural Redhead)
 * still surfaces the STATE and COUNTRY conversation that bears on the
 * project, instead of returning an empty section:
 *   - `'site'`    — local: city / town / county discourse.
 *   - `'state'`   — statewide news + state-scoped regulatory dockets.
 *   - `'country'` — national discourse (always queried, never empty).
 * Optional for back-compat: a pre-scope entry (undefined) is treated as
 * `'site'` by consumers.
 */
export type PublicSentimentScope = 'site' | 'state' | 'country';

export interface PublicSentimentEntry {
  /** Retained attributed passage; supportingSnippet is only the short display projection. */
  sourcePassage?: string;
  passageTruncated?: boolean;
  sourceId: PublicSentimentSourceId;
  fetchedAt: string;
  btmTech: PublicSentimentBtmTech;
  sentimentScore: number;
  confidence: number;
  supportingSnippet: string;
  url: string;
  /** Geographic layer this observation was gathered at. Undefined on
   *  legacy entries fetched before the three-layer scan landed → 'site'. */
  scope?: PublicSentimentScope;
  /** Date stated by the source; never substitute retrieval time. */
  observedDate?: string;
  /** Exact source date excerpt verified by grounded worker extraction. */
  sourceDateQuote?: string;
  /** Exact normal-document witness; private origin stays separate from API provenance. */
  documentSource?: {
    documentId: string; originalFileSha256: string; bodySha256: string;
    charStart: number; charEnd: number; attribution: EvidenceAttribution;
    demonstrationProvenance?: import('./demonstration').DemonstrationDocumentProvenanceV1;
  };
  dateBasis?: 'published' | 'event' | 'scheduled-event' | 'first-seen';
  scopeBasis?: 'verified-local-source' | 'named-locality' | 'regional-context';
  sourceSentimentScore?: number;
  sourceConfidence?: number;
  stanceKind?: 'public-opinion' | 'policy-action' | 'unknown';
  assessmentStatus?: 'assessed' | 'unscored';
  assessmentReason?: string;
}

/**
 * Per-tech aggregation emitted by `aggregatePublicSentiment`. Each
 * entry pins the count + mean sentiment for one BTM tech bucket. The
 * five buckets are always present in the output (count: 0 + mean: 0
 * for tech families with no observations) so downstream consumers
 * (scoring axis + the renderer) don't have to guard on missing keys.
 */
export interface PerTechSentiment {
  btmTech: PublicSentimentBtmTech;
  /** Number of observations contributing to this bucket. */
  count: number;
  /**
   * Confidence-weighted mean sentiment in [−1, +1]. Zero when count is
   * zero (no observations to average).
   */
  meanSentiment: number;
  /**
   * Weighted-mean confidence in [0, 1]. Zero when count is zero.
   * Distinct from `count` — a single high-confidence GDELT story is
   * worth more than three low-confidence ones.
   */
  meanConfidence: number;
}

/**
 * Per-geographic-scope aggregation emitted alongside `perTech`. Lets the
 * report present the sentiment picture at each layer (site / state /
 * country) rather than collapsing them, so a reviewer can see that —
 * e.g. — local discourse is silent while statewide and national
 * sentiment toward the technology is mixed. Confidence-weighted mean,
 * same convention as `PerTechSentiment`.
 */
export interface PerScopeSentiment {
  scope: PublicSentimentScope;
  /** Number of observations gathered at this geographic layer. */
  count: number;
  /** Confidence-weighted mean sentiment in [−1, +1]. Zero when count is zero. */
  meanSentiment: number;
  /** Weighted-mean confidence in [0, 1]. Zero when count is zero. */
  meanConfidence: number;
}

/**
 * Payload of the `publicSentiment` SED section. Mirrors the shape of
 * `ExternalLiteratureSection` so the two orchestrators share testing +
 * rendering patterns.
 *
 *  - `status: 'ok'`           — at least one source returned entries.
 *  - `status: 'partial'`      — some sources failed; the entries we
 *    have are still usable. `reason` carries which sources failed.
 *  - `status: 'unavailable'`  — every source failed OR no observations
 *    after dedup; `entries` is empty. `reason` describes why.
 */
/** PSR Phase 2 — tier-1 demographic / civic fact from deep fetchers. */
export interface PublicSentimentTier1Fact {
  sourceId: string;
  key: string;
  label: string;
  value: string | number;
  units?: string;
  url?: string;
  observedAt: string;
}

/** PSR Phase 2 — citation anchor for G1 verifier from tier-1 facts. */
export interface PublicSentimentTier1CitationRef {
  index: number;
  label: string;
  url: string;
  sourceId: string;
  factKey: string;
}

export interface PublicSentimentSourceLead {
  url: string; title: string; sourceQuote?: string; summary?: string; publicationDate: string | null;
  fetchedAt: string; locality?: string; namedProject?: string | null; classification: string; limitations: string[];
}

export interface PublicSentimentCollectionContext {
  schemaVersion: 1;
  point: { lat: number; lng: number };
  pointBasis: 'selected-study-area-centroid' | 'legacy-site-location';
  studyAreaSelectionHash?: string;
  studyAreaGeometryHash?: string;
  address: string;
  state?: string;
  county?: { stateFips: string; countyFips: string; countyName: string; stateCode: string; blockFips?: string; blockBbox?: [number, number, number, number]; sourceUrl?: string; censusCounty?: { rings: number[][][] } };
  countySourceUrl?: string;
  countyResolutionError?: string;
}

export interface PublicSentimentSection {
  collectionContext?: PublicSentimentCollectionContext;
  discoveryExclusions?: Array<{ url: string; reason: string; observedDate?: string; fetchedAt: string }>;
  municipalArchiveAudit?: Array<{ listedAttachments?: number; readAttachments?: number; queryPoint?: { lat: number; lng: number }; queryPointBasis?: 'selected-study-area-centroid' | 'legacy-site-location'; htmlDocuments?: number; indexUrls?: string[]; countyFips?: string; countySourceUrl?: string; bodyCacheHits?: number; archiveUrl: string; jurisdiction: string; fetchedAt: string; listedDocuments: number; inWindowDocuments: number; readDocuments: number; readPages: number; relevantDocuments: number; extractionMethod?: 'native-pdf-text-with-vision-ocr' | 'native-pdf-text-with-vision-ocr-and-html'; ocrPages?: number; ocrCacheHits?: number; scopeNote?: string; errors: string[] }>;
  refreshedFromSedId?: string;
  sourceVerification?: { sourceSedId: string; documentInputHash: string; verifiedAt: string; evidenceReferences: string[] };
  /** Unverified discoveries are retained for review; they never contribute to score/trend. */
  sourceLeads?: PublicSentimentSourceLead[];
  /** Hash of the current source-validated uploaded consultation observations, before caps. */
  documentSourceIdentity?: string;
  queryContext?: import('../lib/sentiment/publicSentimentScope').SentimentQueryContext;
  assessment?: import('../lib/sentiment/publicSentimentScope').SentimentAssessment;
  history?: Array<{ fetchedAt: string; sourceSedId?: string; queryFingerprint?: string; entryCount: number; assessment?: import('../lib/sentiment/publicSentimentScope').SentimentAssessment }>;
  change?: { addedObservationCount: number; comparable: boolean; supportIndexDelta?: number };
  sourceCoverage?: Array<{ sourceId: string; coverage: string; errors: string[] }>;

  status: 'ok' | 'partial' | 'unavailable';
  entries: PublicSentimentEntry[];
  /** Per-tech aggregates the scoring axis reads. Always 5 entries. */
  perTech: PerTechSentiment[];
  /** Per-geographic-layer aggregates (site / state / country). Always 3
   *  entries when the three-layer scan ran; omitted on legacy sections. */
  perScope?: PerScopeSentiment[];
  /** Query strings issued to the upstream sources (audit trail). */
  queriesIssued: string[];
  sourcesAttempted: PublicSentimentSourceId[];
  fetchedAt: string;
  reason?: string;
  /** PSR Phase 2 — tier-1 normalized facts (census, BLS, FCC, open data, GIS, news). */
  tier1Facts?: PublicSentimentTier1Fact[];
  tier1CitationRefs?: PublicSentimentTier1CitationRef[];
  tier1SourcesAttempted?: string[];
}

/**
 * Cost Phase P4 — per-section input fingerprint (incremental SED).
 *
 * The unit `computeSedDirtySet` compares against to decide whether a section's
 * inputs changed since the last compile. Stamped by `stampSedFingerprints`.
 */
export interface SectionFingerprint {
  /**
   * FNV-1a hex over the section's input bundle: provider-input bundle
   * (coords + provider config) + linked doc contentHashes + relevant
   * intent-field values + EXTRACTION_MODEL_VERSION + upstream sections'
   * inputHashes. A change here ⇒ the section is dirty.
   */
  inputHash: string;
  /** ISO timestamp the fingerprint was computed. */
  computedAt: string;
  /** EXTRACTION_MODEL_VERSION at compile time. A global bump invalidates all. */
  modelVersion: string;
}

/**
 * P4 — one topic of an uploaded document's content index, projected into
 * the SED so the whole-SED prompt can see what a document says beyond its
 * summary, and who said it.
 */
export interface SedDocContentIndexEntry {
  topic: string;
  summary: string;
  page?: number;
  attribution?: EvidenceAttribution;
}

export interface SiteEvidenceDocument {
  /** Exact owner-hydrated SED-input document manifest. */
  demonstrationProvenance?: import('./demonstration').FrozenDemonstrationBasisV1;
  /** Exact private reference-data source basis; retained in semantic identity. */
  refdataAcceptance?: import('./refdata').RefdataAcceptanceSourceBinding;
  // ── Metadata ──
  id: string;
  siteId?: string;
  version: number;
  siteName: string;
  location: { lat: number; lng: number; address: string };
  collectedAt: string;
  collectionDurationMs: number;
  /**
   * R-33 — ISO build timestamp (alias of `collectedAt` on new SEDs).
   * Optional on legacy payloads; readers fall back to `collectedAt`.
   */
  builtAt?: string;
  /**
   * R-33 — `hashProjectIntent` fingerprint at bootstrap time. Drives
   * wizard `shouldBootstrap` without re-diffing full intent objects.
   */
  builtForIntentHash?: string;
  /** Optional explicit selected-assessment applicability bound during normal SED compilation. */
  builtForGenerationAssessmentHash?: string;
  /** Exact curated document extraction/context identity consumed by this SED. */
  documentInputHash?: string;
  studyAreaScope?: import('./siteStudyArea').SiteStudyAreaScope;

  /**
   * Snapshot of the owning company's archetype at SED-collection time.
   * Threaded into report-generation prompts so the narrative speaks to
   * the right reader (hyperscaler vs. utility vs. consultancy) without
   * the report-side code having to re-resolve the company record.
   * Optional — undefined for legacy SEDs, in which case prompts fall
   * back to neutral developer/owner framing.
   */
  company?: {
    id?: string;
    name?: string;
    archetype?: import('./company').CompanyArchetype;
    archetypeLabel?: string;
    archetypeNote?: string;
  };

  // ── Evidence sections (one per data domain) ──
  climate: EvidenceSection<ClimateEvidence>;
  waterStress: EvidenceSection<WaterStressEvidence>;
  seismic: EvidenceSection<SeismicEvidence>;
  grid: EvidenceSection<GridEvidence>;
  flood: EvidenceSection<FloodEvidence>;
  environmental: EvidenceSection<EnvironmentalEvidence>;
  nuclearInfra: EvidenceSection<NuclearInfraEvidence>;
  /** Reactor design class from Project Intent — optional on legacy SEDs. */
  technology?: EvidenceSection<TechnologyEvidence>;
  /**
   * v1.2.1 HR-5 P2-2 — first-class BTM cooling section (3 axes + resolved PUE +
   * water-by-sink + waste-heat credit). Intent-stamped; ADDITIVE + optional so
   * legacy SEDs load unchanged.
   */
  cooling?: EvidenceSection<CoolingEvidence>;
  /**
   * v1.2.1 HR-5 P2-3 — basis-explicit power semantics (IT-load vs facility vs
   * MVA). Intent-stamped; ADDITIVE + optional.
   */
  power?: EvidenceSection<PowerSemanticsEvidence>;
  /**
   * v1.2.1 HR-5 P2-3 — firm-capacity / ELCC accreditation intent.
   * Intent-stamped; ADDITIVE + optional.
   */
  firmCapacity?: EvidenceSection<FirmCapacityEvidence>;
  /**
   * v1.2.1 HR-5 P2-3 — LCOE economics anchor with explicit fuel-cost grounding.
   * Sourced from financialAssumptions; ADDITIVE + optional.
   */
  lcoe?: EvidenceSection<LcoeEvidence>;
  /**
   * v1.2.1 HR-5 P2-4 — natural-gas / fuel-supply section grounding the gas-BTM
   * fuel cost (EIA price + KMZ pipeline proximity). Provider-grounded; ADDITIVE
   * + optional.
   */
  fuelSupply?: EvidenceSection<FuelSupplyEvidence>;
  /**
   * SED D2 — typed grid-interconnection evidence (ISO/RTO queue position +
   * declared interconnection status). Provider-grounded (iso-queue) +
   * intent-stamped; grounds the power-availability verdict. ADDITIVE + optional
   * so legacy SEDs load unchanged.
   */
  interconnection?: EvidenceSection<InterconnectionEvidence>;
  /**
   * SED D2 — typed IRA-incentives evidence (§45Y / §48E adders). Sourced from
   * the pure `calculateTaxCredits` calculator off `financialAssumptions`.
   * ADDITIVE + optional.
   */
  incentives?: EvidenceSection<IncentivesEvidence>;
  /**
   * SED D2 (H-6) — typed MULTI-HAZARD evidence (FEMA National Risk Index:
   * wildfire / strong-wind / hail / drought + composite Risk Index), beyond
   * the SED's existing `flood` (NFHL) + `seismic` (USGS) sections.
   * Provider-grounded via the deterministic `femaNriProvider`. Honestly
   * `unavailable` for non-US / off-territory sites. ADDITIVE + optional.
   */
  multiHazard?: EvidenceSection<MultiHazardEvidence>;
  /**
   * SED D2 (H-6) — typed TRANSMISSION-HEADROOM evidence (ATC MW, hosting
   * capacity MW, nodal LMP, congestion). Honest-unavailable today (no clean
   * point-queryable public API in-repo; real sources named as follow-up —
   * never fabricated). ADDITIVE + optional.
   */
  transmissionHeadroom?: EvidenceSection<TransmissionHeadroomEvidence>;
  /**
   * SED D2 (H-6) — typed WATER-RESOURCE evidence (consumptive-use AFY,
   * water-rights, aquifer depth, NPDES), distinct from the basin-level
   * `waterStress` section. Honest-unavailable today (state-administered /
   * well-id-keyed sources; real sources named as follow-up — never
   * fabricated). ADDITIVE + optional.
   */
  waterResource?: EvidenceSection<WaterResourceEvidence>;
  /** Screening DEM terrain; optional for legacy and non-US sites. */
  topography?: EvidenceSection<TopographyEvidence>;
  /** Public SSURGO screen plus separately sourced professional facts. */
  geotechnical?: EvidenceSection<GeotechnicalEvidence>;
  /** Professional survey evidence; public parcel data cannot satisfy it. */
  survey?: EvidenceSection<SurveyEvidence>;
  /** Public occurrence screen plus document-only mineral title facts. */
  mineralResources?: EvidenceSection<MineralResourcesEvidence>;
  /** Public listed-property screen plus sensitive professional survey facts. */
  culturalResources?: EvidenceSection<CulturalResourcesEvidence>;
  solar: EvidenceSection<SolarEvidence>;
  wind: EvidenceSection<WindEvidence>;
  electricity: EvidenceSection<ElectricityEvidence>;
  airQuality: EvidenceSection<AirQualityEvidence>;
  connectivity: EvidenceSection<ConnectivityEvidence>;
  parcel: EvidenceSection<ParcelEvidence>;
  regulatory: EvidenceSection<RegulatoryEvidence>;
  siteResearch: EvidenceSection<import('../services/dataProviders/siteResearch').SiteResearchData>;
  adamsDocuments: EvidenceSection<AdamsDocEvidence[]>;
  siteProfile: EvidenceSection<SiteProfileEvidence>;
  /**
   * Public workforce, deterministic economic screening, and policy-valid
   * comparable-project evidence. Optional for legacy SEDs.
   */
  economicEvidence?: EvidenceSection<EconomicEvidenceData>;

  // ── Knowledge & Document Context ──
  knowledgeBase: { id: string; topic: string; findings: string; source: string }[];
  documentEvidenceQuality?: import('../lib/sed/documentEvidenceDigest').DocumentEvidenceQuality;
  documentFacts?: DocumentEvidenceFact[];
  /** Source-stated site resources, distinct from surveyed gross area and utility system capacity. */
  siteResourceDocumentEvidence?: import('./siteResourceEvidence').SiteResourceDocumentEvidence;
  generationDocumentEvidence?: { documentId: string; documentName: string; extraction: NonNullable<import('./siteRag').SiteDocument['generationScreeningExtraction']> }[];
  uploadedDocuments: { fileName: string; summary: string; tags: string[]; textContent?: string; priority?: boolean; pages?: { page: number; charStart: number; charEnd: number; text: string }[]; contentIndex?: SedDocContentIndexEntry[]; visualReadings?: import('./siteRag').DocumentVisualReading[]; parentDocId?: string; docId?: string; evidenceDigest?: import('../lib/sed/documentEvidenceDigest').DocumentEvidenceDigest }[];

  // ── Industry benchmarks (derived from knowledge base + ADAMS, not hardcoded) ──
  benchmarks?: import('../services/industryBenchmarks').IndustryBenchmarks;

  // ── User-defined project parameters ──
  projectIntent?: {
    /** Authoritative workload classification (new). */
    workloadProfile?: string;
    /** @deprecated Legacy field kept on existing records; new SEDs write workloadProfile. */
    projectType?: string;
    powerRequirement: string;
    /** PM pack — exact MW when the user provided one; preferred over the bucket. */
    powerRequirementMW?: number;
    coolingStrategy: string;
    /**
     * BTM cooling-GENERATION machine axis (absorption / vapour-compression /
     * steam-driven / free-cooling). ADDITIVE + optional — orthogonal to
     * `coolingStrategy` (rack-side) and the cold-end heat-rejection sink.
     * Type-only mirror of `ProjectIntent.coolingGeneration`; the prose
     * projection lives in the siteEvidence SERVICE file, not here.
     */
    coolingGeneration?: import('./siteRag').CoolingGeneration;
    /**
     * GAP 3 (field-architecture wiring) — the cold-END heat-rejection SINK
     * (dry-cooler / wet-cooling-tower / hybrid / once-through / absorption-cchp
     * / free-cooling). This is the axis that drives WATER WITHDRAWAL — distinct
     * from the rack-side `coolingStrategy` (air vs liquid) and the BTM
     * `coolingGeneration` machine. ADDITIVE + optional; type-only mirror of
     * `ProjectIntent.bopHeatRejection`. Surfaced into the sparse view so the
     * SED-variable browser + completeness machinery can see the sink, not just
     * the rack-side loop.
     */
    bopHeatRejection?: import('./siteRag').BopHeatRejection;
    nuclearInterest: string;
    timeline: string;
    /** PMT pack — exact target COD (ISO yyyy-mm-dd) when the user provided one. */
    inServiceDate?: string;
    priority: string;
  };
  /**
   * SOC — canonical full `ProjectIntent` persisted at SED-compile time.
   * Optional for back-compat with legacy SEDs (pre-SOC). New SEDs always
   * write this when the site has a `projectIntent` set, in addition to
   * the legacy sparse `projectIntent` view above (which is derived via
   * `deriveSparseProjectIntent` so existing readers don't break).
   *
   * The gen-time prompt continues to read live values through
   * `buildInputLayersBlock` (IC4); this field is a historical record
   * for the rerun-diff layer (RDF) and an audit trail.
   */
  fullProjectIntent?: import('./siteRag').ProjectIntent;
  /**
   * Immutable Generation & Impact scenario selected at promotion time.
   * This is copied into each rebuilt SED so historical reports do not depend
   * on a mutable subcollection lookup.
   */
  promotedGenerationScenarioSnapshot?: import('./generationImpact').GenerationImpactScenarioSnapshot;
  /**
   * Versioned scenario + immutable layout publication frozen by the SED
   * rebuild. Test Fit uses this authority contract instead of mixing live
   * Project Intent with mutable map state.
   */
  generationImpactAuthoritySnapshot?: import('./generationImpact').GenerationImpactSedAuthoritySnapshot;
  /**
   * SOC — user-declared site context (claims + prose + conflicts)
   * snapshot at SED-compile time. The gen-time prompt continues to read
   * live `siteContext` from the store via `buildSiteContextBlock` (SC8);
   * this field is a historical record for the rerun-diff layer.
   *
   * Note: `siteContext.text` may be many KB. `sedToText` truncates the
   * prose snapshot at 5K chars (with `[... truncated]` marker); the
   * persisted SED carries the full text so a later analysis can reload
   * it intact.
   */
  siteContext?: import('./siteContext').SiteContext;
  /**
   * SOC — financial assumptions (core + extended) snapshot at SED-compile
   * time. Persisted historical record only — the live LCOE-formula path
   * runs through `report.financialAssumptionsAtGen` (FA1).
   */
  financialAssumptions?: import('./financialAssumptions').FinancialAssumptions;
  useCase?: string;

  // ── Cross-validation ──
  crossValidation: {
    checks: CrossValidationCheck[];
    overallIntegrity: number;  // 0-1
  };

  // ── Data quality ──
  gaps: DataGap[];
  completeness: number;       // 0-1, fraction of fields with verified data

  /**
   * Coverage mode (Content-Source Fixes Prompt 14).
   *
   *  - `'US'`              — every provider in the catalogue is in
   *    scope; standard pipeline.
   *  - `'global-degraded'` — site is outside US coverage. The
   *    dispatcher skipped US-only providers (HIFLD / FEMA / USFWS /
   *    NREL / EIA / AirNow / NRC / ADAMS / regulatoryDb) without
   *    attempting them; the affected SED slots carry
   *    `reason: 'non-us-coverage'`. Report-side surfaces:
   *      • a banner injected into sedToText before the section dump,
   *      • a confidence cap of 0.65 on `evidenceConfidence`,
   *      • a warning chip in the New Report wizard.
   *
   * Optional for back-compat with SEDs written before Prompt 14
   * landed — the renderer treats undefined as `'US'`.
   *
   * SED D3.4 — the union widens with two GRANULAR degraded modes so the
   * binary US-vs-not classification can distinguish a US territory (where
   * the federal provider fleet is partial but the site IS in the US) from
   * a fully non-US site. `'US'` | `'global-degraded'` are kept for
   * back-compat (readers default `undefined`→`'US'`). The richer detail —
   * WHICH US-federal providers were skipped and which sections went dark —
   * lives on `coverageDegradation` below.
   */
  coverageMode?: 'US' | 'global-degraded' | 'us-territory-degraded' | 'tribal-degraded';

  /**
   * SED D3.4 — explicit, first-class off-territory degradation record.
   * The dispatcher already SKIPS US-only providers for non-US sites and
   * stamps `coverageMode`; this rollup makes the degradation a quality
   * INPUT instead of something every downstream surface has to re-derive.
   *
   *  - `mode`                  — the `coverageMode` that triggered the
   *                              degradation.
   *  - `territory`             — the fine-grained `TerritoryClass`
   *                              (`'conus'`, `'us-territory'`, …) from
   *                              `classifyTerritory`.
   *  - `usOnlyProvidersSkipped`— the exact `US_ONLY_PROVIDER_IDS` that were
   *                              skipped for this site.
   *  - `affectedSections`      — the SED sections that went dark as a
   *                              result (section-id strings; the per-claim
   *                              detail is NOT duplicated here, preserving
   *                              the doc-size budget).
   *  - `note`                  — human-readable summary for the report
   *                              appendix.
   *
   * Optional + additive — absent on every US (`coverageMode === 'US'` or
   * undefined) site and on every legacy SED. NO scoring change: this is a
   * surfacing/observability field; the off-territory honesty is expressed
   * as a `DataGap` (which already counts in coverage), not a new penalty.
   */
  coverageDegradation?: {
    mode: 'global-degraded' | 'us-territory-degraded' | 'tribal-degraded';
    territory?: 'conus' | 'ak-hi' | 'us-territory' | 'tribal-trust-uncertain' | 'non-us';
    usOnlyProvidersSkipped: string[];
    affectedSections: SedSectionId[];
    note: string;
  };

  /**
   * SED D3.3 — cross-source triangulation rollup. Historically the SED
   * only modelled DISAGREEMENT (`evidenceConflicts`); this rollup rewards
   * cross-source AGREEMENT. Computed by `computeTriangulation` (pure,
   * `src/lib/sed/sedTriangulation.ts`) on every rebuild from the recompute
   * hook. Per material slot it counts INDEPENDENT corroborating sources
   * (provider + doc + report-claim + authored, deduped by source family).
   *
   *  - `signals`        — one `TriangulationSignal` per material slot
   *                       (≥2 corroborating sources, or partial agreement).
   *                       Capped to material slots to preserve doc size.
   *  - `meanAgreement`  — 0..1 mean agreement across the signals.
   *
   * Optional + additive — legacy SEDs read `undefined`. NO scoring change
   * UNTIL the default-OFF `PORTFOLIO_CONFIG.ENABLE_D3_TRIANGULATION_SCORING`
   * flag is flipped on (then `meanAgreement` feeds a small capped
   * confidence BONUS in `computeDataConfidencePenalty`). Neutral when off.
   */
  triangulation?: {
    signals: Array<{
      sectionId: string;
      field: string;
      agreeingSources: string[];
      agreementScore: number;
    }>;
    meanAgreement: number;
  };

  // ── Expert amendments ──
  amendments: Amendment[];

  /**
   * SED-FOUNDATION — evidence-workflow items (open-questions, verification
   * statuses, siting notes). OPTIONAL + additive: legacy SEDs without the
   * field behave exactly as today (`undefined` reads as "no workflows", and
   * the readiness folding term contributes 0 penalty). Items are
   * versioned/auditable and supersede on report re-ingestion via
   * `idempotencyKey`. Operator answers are projected here as attributable
   * expert-judgment siting notes; automatic ingestion of every newly generated
   * report question remains deferred.
   */
  workflows?: EvidenceWorkflowItem[];

  /**
   * Documents_On_Site Prompt 12 — provider/document conflicts detected
   * after Gemini comparison. Persisted on the SED so the section
   * editor + report quality panel can render flags without re-running
   * Gemini per render. Each entry can carry a userResolution once the
   * admin picks one.
   */
  evidenceConflicts?: import('../services/evidenceConflict').EvidenceConflict[];

  /**
   * ICR-FU-3 — version stamp of the conflict-detection pipeline that
   * produced `evidenceConflicts`. Bumped (via
   * `CONFLICT_PIPELINE_VERSION` in `src/lib/conflictPrefilter.ts`)
   * whenever the prefilter's behaviour changes in a way that should
   * invalidate cached rows. Absent ⇒ the SED was last written by a
   * pre-ICR-FU pipeline; the bootstrap discards `evidenceConflicts`
   * on the next rebuild so the new detector re-evaluates from scratch.
   *
   * Back-compat: legacy SEDs without this field continue to load
   * normally; only the merge step at rebuild time consults it.
   */
  conflictsPipelineVersion?: number;

  /**
   * Cost Phase P4 — per-section input fingerprints (incremental SED).
   *
   * Stamped on every compile by `stampSedFingerprints` (sedFingerprint.ts).
   * Each entry records the FNV-1a `inputHash` over the section's
   * provider-input bundle + linked doc contentHashes + relevant intent-field
   * values + EXTRACTION_MODEL_VERSION + upstream sections' inputHashes. The
   * incremental rebuild path (`computeSedDirtySet`) compares the live
   * fingerprint against this stamp to decide which sections changed.
   *
   * OPTIONAL + additive: legacy SEDs without the field are treated as
   * "no prior fingerprints" → the incremental path falls back to a FULL
   * rebuild (which stamps fingerprints), so the next rebuild is incremental.
   * Absence NEVER causes an under-recompute (a section without a prior
   * fingerprint is conservatively DIRTY).
   */
  sectionFingerprints?: Partial<Record<SedSectionId, SectionFingerprint>>;

  /** Declared inputs actually consumed by scoped pure producers. Worklist-only:
   * never replaces canonical provider/doc fingerprints, truth or quality gates. */
  sectionIntentInputHashes?: Partial<Record<SedSectionId, string>>;

  // ── Fact-check results (populated after AI stages) ──
  factCheck?: {
    verified: number;
    unverified: number;
    contradictions: string[];
    score: number;            // 0-1
  };

  /**
   * Multi-Parcel Site Boundary (MP pack, MP4) — number of parcels the
   * site is composed of. Always >= 1 when `parcels` is populated;
   * undefined for legacy SEDs (treated as 1).
   */
  parcelCount?: number;
  /**
   * MP pack: true when any two parcels are non-contiguous (Haversine
   * gap between nearest vertices > 100 m). Drives report-side
   * "non-contiguous" copy + provider-call branching.
   */
  isDisjoint?: boolean;
  /**
   * MP pack: per-parcel SED facts. Only emitted when `parcelCount > 1`
   * (single-parcel SEDs collapse into the existing flat fields). When
   * present, the per-type renderers use these to call out parcel
   * disagreement (e.g. one parcel in FEMA X, another in AE) and the
   * LLM prompt receives a `[PARCELS]` block with one entry per parcel.
   */
  parcels?: Array<{
    id: string;
    label: string;
    area: number;                                  // acres
    centroid: { lat: number; lng: number };
    intendedUse?: string;
    /** Per-parcel elevation aggregate (m ASL, area-weighted upstream). */
    elevationAvg?: number | null;
    /** Per-parcel FEMA flood zone code (X / AE / VE / …). */
    floodZone?: string | null;
    /** Per-parcel ASCE seismic design category (A-F). */
    seismicSDC?: string | null;
    /** Per-parcel zoning code (free-form, regrid output). */
    zoning?: string | null;
  }>;

  /**
   * KSD1 — KMZ-derived structured site infrastructure facts.
   *
   * Populated by `compileOnSiteInfrastructure` (KSD2) AFTER
   * `compileSiteEvidence` finishes. When the site has no `siteLayers`
   * the section reads `status: 'unavailable'`; with layers it reads
   * `status: 'verified-from-kmz'` and the structured arrays are
   * populated.
   *
   * Cross-binding: KSD2 lifts the closest substation distance into
   * `grid.data.substationDistKm` (provenance flips to `'kmz-import'`)
   * and the closest fiber corridor distance into a notional
   * `connectivity` enrichment. The original provider values are kept
   * in `grid.source` so the audit trail remains intact.
   *
   * Optional for back-compat: SEDs written before KSD1 omit the field;
   * readers should treat that as "no KMZ data on this site".
   */
  onSiteInfrastructure?: Omit<EvidenceSection<OnSiteInfrastructureData>, 'status'> & {
    /** KSD1 widens `status` to include `'verified-from-kmz'`. */
    status: OnSiteInfrastructureStatus;
  };

  /**
   * LL1 (Live Literature Retrieval) — externally retrieved peer-reviewed
   * + preprint sources (Crossref, arXiv, PubMed). Populated by
   * `externalLiteratureOrchestrator` and woven into the SED in
   * `compileSiteEvidence`. Surfaced in `sedToText` under
   * `## RELEVANT PUBLICATIONS` and appended to the reference appendix
   * by `assembleReferences()` as `[Ref N]` entries (after siteResearch
   * — existing refs are NOT renumbered).
   *
   * Optional for back-compat: SEDs written before LL1 have no field;
   * readers should treat absence as "literature retrieval not run".
   * Distinct from `status: 'unavailable'`, which means it WAS run but
   * every provider failed.
   */
  externalLiterature?: ExternalLiteratureSection;

  /**
   * PSE-1 (Public Sentiment Engine) — per-BTM-tech sentiment from
   * GDELT 2.0, MuniCode/Granicus/Civic-Plus, and Regulations.gov /
   * FERC eLibrary / NRC ADAMS docket activity. Populated by
   * `publicSentimentOrchestrator` and aggregated into the 5 BTM tech
   * buckets exposed on `publicSentiment.perTech`.
   *
   * Optional for back-compat: SEDs written before PSE-1 have no
   * field; readers MUST treat absence as "sentiment not collected"
   * and the scoring axis MUST redistribute its weight to the other
   * categories (see `scoreSite` in `siteRagService.ts`). Distinct
   * from `status: 'unavailable'`, which means the orchestrator ran
   * but every source failed.
   */
  publicSentiment?: PublicSentimentSection;

  /**
   * SCM v2 — live supply-chain plan snapshot stamped before supply-chain-risk
   * report generation. Optional for back-compat: SEDs without this field were
   * generated before the SCM narrative rework.
   */
  supplyChainSnapshot?: EvidenceSection<SupplyChainSnapshotEvidence>;
}

/**
 * AI analysis output from Stage 1 (structured, not narrative).
 */
export interface StructuredAnalysis {
  sedId: string;
  sedVersion: number;
  analyzedAt: string;

  // Top findings — each must cite a SED section
  findings: {
    rank: number;
    title: string;
    detail: string;
    sedSection: string;       // which SED section this references
    impact: 'positive' | 'negative' | 'neutral';
    confidence: number;
    pageReference?: number;
  }[];

  // Category scores (deterministic from SED)
  scores: {
    overall: number;
    grid: number;
    cooling: number;
    site: number;
    regulatory: number;
    logistics: number;
  };

  // Risks with SED traceability
  risks: {
    label: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    sedSection: string;
    dataPoint: string;        // specific value that triggers this risk
    mitigation: string;
  }[];

  // Data gaps that affect the conclusion
  materialGaps: {
    field: string;
    wouldChangeConclusion: boolean;
    recommendation: string;
  }[];

  // Go/No-Go
  recommendation: 'Proceed' | 'Proceed with Conditions' | 'Do Not Proceed';
  conditions: string[];
}
